<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { usePlatformBranding } from '@/composables/usePlatformBranding'

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

const { isWhitelabel, platformBaseUrl, loadPlatformBranding } = usePlatformBranding()
onMounted(() => loadPlatformBranding())

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
type EndpointScope = 'Guest' | 'Member' | 'Staff' | 'Server'
type CodeLang = 'javascript' | 'php' | 'nodejs'

type ApiHeader = {
  key: string
  value: string
}

type ApiBody = Record<string, unknown> | Array<unknown>

type ApiEndpoint = {
  id: string
  method: HttpMethod
  path: string
  type: EndpointScope
  desc: string
  headers: ApiHeader[]
  auth?: boolean
  body?: ApiBody
}

type ApiCollection = {
  name: string
  icon: string
  endpoints: ApiEndpoint[]
}

type SnippetLanguageOption = {
  key: CodeLang
  label: string
}

type ScopeFilter = 'all' | EndpointScope
type AuthFilter = 'all' | 'auth' | 'open'
type ResponseView = 'preview' | 'raw'

type RequestHistoryItem = {
  id: string
  endpointId: string
  method: HttpMethod
  path: string
  desc: string
  collectionName: string
  scope: EndpointScope
  requiresAuth: boolean
  status: number | null
  durationMs: number | null
  createdAt: string
}

// Global Auth State for Testing
const globalToken = ref(localStorage.getItem('dev_token') || '')
const globalAppId = ref(localStorage.getItem('dev_app_id') || '')
const globalAppPublicToken = ref(localStorage.getItem('dev_app_public_token') || '')
const globalResourceId = ref(localStorage.getItem('dev_resource_id') || '1')
const editablePath = ref('')

const search = ref('')
const scopeFilter = ref<ScopeFilter>('all')
const authFilter = ref<AuthFilter>('all')
const selectedEndpoint = ref<ApiEndpoint | null>(null)
// Il pannello di dettaglio sta piu' in basso nella stessa colonna: senza un
// popup esplicito, cliccare una card della griglia (che resta visibile in
// alto) non dava alcun segnale che la selezione fosse davvero cambiata.
const endpointDialogOpen = ref(false)
const activeTab = ref<'body' | 'headers' | 'code'>('body')
const selectedLang = ref<CodeLang>('javascript')
const copyFeedback = ref('')
const lastDurationMs = ref<number | null>(null)
const responseView = ref<ResponseView>('preview')
const requestHistory = ref<RequestHistoryItem[]>([])
const snippetLanguages: SnippetLanguageOption[] = [
  { key: 'javascript', label: 'JAVASCRIPT' },
  { key: 'php', label: 'PHP' },
  { key: 'nodejs', label: 'NODEJS' },
]
const setSelectedLang = (lang: CodeLang) => {
  selectedLang.value = lang
}
const historyStorageKey = 'dev_api_request_history'

// Request/Response State
const isSending = ref(false)
const lastResponse = ref<unknown>(null)
const lastStatus = ref<number | null>(null)

const collections: ApiCollection[] = [
  {
    name: 'Auth & Session',
    icon: 'tabler-lock',
    endpoints: [
      { id: 'auth-register', method: 'POST', path: '/auth/register', type: 'Guest', desc: 'Create a customer account from the app registration flow.', headers: [{ key: 'Content-Type', value: 'application/json' }], body: { name: 'Mario Rossi', email: 'mario@example.com', password: 'secret123', password_confirmation: 'secret123' } },
      { id: 'auth-login', method: 'POST', path: '/auth/login', type: 'Guest', desc: 'Email/password login for the mobile app.', headers: [{ key: 'Content-Type', value: 'application/json' }], body: { email: 'mario@example.com', password: 'secret123' } },
      { id: 'auth-social-login', method: 'POST', path: '/auth/social-login', type: 'Guest', desc: 'Social sign-in callback payload for supported providers.', headers: [{ key: 'Content-Type', value: 'application/json' }, { key: 'X-App-Id', value: '{{APP_ID}}' }], body: { provider: 'google', token: 'PROVIDER_ACCESS_TOKEN' } },
      { id: 'auth-forgot-password', method: 'POST', path: '/auth/forgot-password', type: 'Guest', desc: 'Request a password reset email for an app user.', headers: [{ key: 'Content-Type', value: 'application/json' }, { key: 'X-App-Id', value: '{{APP_ID}}' }], body: { email: 'mario@example.com' } },
      { id: 'auth-reset-password', method: 'POST', path: '/auth/reset-password', type: 'Guest', desc: 'Complete password reset with token, email, and new password.', headers: [{ key: 'Content-Type', value: 'application/json' }], body: { token: 'RESET_TOKEN', email: 'mario@example.com', password: 'new-secret123', password_confirmation: 'new-secret123' } },
      { id: 'auth-email-resend', method: 'POST', path: '/auth/email/resend', type: 'Guest', desc: 'Resend email verification for a pending account.', headers: [{ key: 'Content-Type', value: 'application/json' }], body: { email: 'mario@example.com' } },
      { id: 'auth-providers-public', method: 'GET', path: '/public/auth/providers', type: 'Guest', desc: 'Public social-login provider catalog resolved for the current tenant app.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'auth-user', method: 'GET', path: '/user', type: 'Member', auth: true, desc: 'Resolve the authenticated app user payload and active tenant/app context.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'auth-logout', method: 'POST', path: '/auth/logout', type: 'Member', auth: true, desc: 'Invalidate the current Sanctum token and close the app session.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
    ],
  },
  {
    name: 'App Bootstrap & Legal',
    icon: 'tabler-rocket',
    endpoints: [
      { id: 'runtime-mobile-settings', method: 'GET', path: '/public/tenant/mobile-app-settings', type: 'Guest', desc: 'Public runtime bootstrap for branding, enabled modules, home layout, and install settings.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'runtime-i18n', method: 'GET', path: '/public/tenant/i18n/{code}', type: 'Guest', desc: 'Load localized mobile runtime strings for the selected language code.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'runtime-legal', method: 'GET', path: '/public/tenant/legal/{pageType}', type: 'Guest', desc: 'Fetch privacy, cookies, or terms content for the current tenant app.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'runtime-config', method: 'GET', path: '/public/tenant/config', type: 'Guest', desc: 'Public tenant/app bootstrap with store name, currency, payment methods, and active features.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'runtime-categories', method: 'GET', path: '/public/tenant/categories', type: 'Guest', desc: 'Public app categories used by runtime menus and home layouts.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'runtime-image-proxy', method: 'GET', path: '/public/tenant/image-proxy?p={encodedPath}', type: 'Guest', desc: 'Proxy public tenant images to avoid browser CORS issues in web clients.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'store-config', method: 'GET', path: '/store/config', type: 'Guest', desc: 'Storefront config with currency, active modules, and public storefront settings.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
    ],
  },
  {
    name: 'Profile & Saved Payments',
    icon: 'tabler-user-circle',
    endpoints: [
      { id: 'account-show', method: 'GET', path: '/account', type: 'Member', auth: true, desc: 'Load the connected customer account profile.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'account-update', method: 'PUT', path: '/account', type: 'Member', auth: true, desc: 'Update the connected customer account details.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { name: 'Mario Rossi', email: 'mario@example.com', phone: '+39 333 1234567' } },
      { id: 'account-delete', method: 'DELETE', path: '/account', type: 'Member', auth: true, desc: 'Delete the current customer account when the app exposes self-delete.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'profile-update', method: 'PUT', path: '/profile/update', type: 'Member', auth: true, desc: 'Lightweight profile update endpoint used by the profile screen.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { full_name: 'Mario Rossi', phone: '+39 333 1234567' } },
      { id: 'profile-password', method: 'PUT', path: '/profile/password', type: 'Member', auth: true, desc: 'Change password for the authenticated customer.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { current_password: 'secret123', password: 'new-secret123', password_confirmation: 'new-secret123' } },
      { id: 'profile-addresses-list', method: 'GET', path: '/profile/addresses', type: 'Member', auth: true, desc: 'List saved customer addresses.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'profile-addresses-create', method: 'POST', path: '/profile/addresses', type: 'Member', auth: true, desc: 'Create a new saved address.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { title: 'Home', address: 'Via Roma 1', city: 'Milano', zip: '20100', country: 'IT', phone: '+39 333 1234567' } },
      { id: 'profile-addresses-update', method: 'PUT', path: '/profile/addresses/{id}', type: 'Member', auth: true, desc: 'Update a saved address.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { title: 'Office', address: 'Via Torino 22', city: 'Milano', zip: '20121', country: 'IT' } },
      { id: 'profile-addresses-delete', method: 'DELETE', path: '/profile/addresses/{id}', type: 'Member', auth: true, desc: 'Delete a saved address.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'profile-recent-addresses', method: 'GET', path: '/profile/recent-order-addresses', type: 'Member', auth: true, desc: 'Get the recent delivery addresses extracted from previous orders.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'social-connections-list', method: 'GET', path: '/social-connections', type: 'Member', auth: true, desc: 'Read linked social providers for the current account.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'social-connections-toggle', method: 'POST', path: '/social-connections', type: 'Member', auth: true, desc: 'Link or unlink a supported social provider.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { provider: 'google', action: 'connect' } },
      { id: 'stripe-public-config', method: 'GET', path: '/settings/stripe/config', type: 'Member', auth: true, desc: 'Fetch the Stripe publishable config resolved for the authenticated app context.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'stripe-setup-intent', method: 'POST', path: '/stripe/setup-intent', type: 'Member', auth: true, desc: 'Create a Stripe SetupIntent to save a card from the app.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'stripe-save-payment-method', method: 'POST', path: '/stripe/save-payment-method', type: 'Member', auth: true, desc: 'Persist a Stripe payment method after SetupIntent confirmation.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { payment_method_id: 'pm_123' } },
      { id: 'stripe-cards-list', method: 'GET', path: '/stripe/cards', type: 'Member', auth: true, desc: 'List saved Stripe cards for the current customer.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'stripe-cards-delete', method: 'DELETE', path: '/stripe/cards/{id}', type: 'Member', auth: true, desc: 'Delete a saved Stripe card.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'stripe-charge-saved', method: 'POST', path: '/stripe/charge-saved', type: 'Member', auth: true, desc: 'Charge a saved Stripe payment method in an app-managed flow.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { payment_method_id: 'pm_123', amount: 1999, currency: 'EUR' } },
    ],
  },
  {
    name: 'Catalog & Ecommerce Checkout',
    icon: 'tabler-shopping-cart',
    endpoints: [
      { id: 'store-categories', method: 'GET', path: '/store/categories', type: 'Guest', desc: 'Full ecommerce categories list for storefront navigation.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'store-locations', method: 'GET', path: '/store/locations', type: 'Guest', desc: 'Store pickup or branch list for ecommerce checkout.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'store-products', method: 'GET', path: '/store/products', type: 'Guest', desc: 'Product catalog with filters such as category_id or search.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'store-product-detail', method: 'GET', path: '/store/products/{id}', type: 'Guest', desc: 'Product detail with variants and pricing.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'store-product-addons', method: 'GET', path: '/store/products/{id}/addons', type: 'Guest', desc: 'Addons and option groups for a specific product.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'store-product-reviews', method: 'GET', path: '/store/products/{id}/reviews', type: 'Guest', desc: 'Public review feed for a specific product.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'store-product-review-create', method: 'POST', path: '/store/products/{id}/reviews', type: 'Guest', desc: 'Submit a product review from the storefront.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { rating: 5, comment: 'Great product!' } },
      { id: 'store-shipping-methods', method: 'POST', path: '/store/shipping/methods', type: 'Guest', desc: 'Resolve available shipping methods for the current cart and destination.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { cart_items: [{ id: 1, quantity: 1 }], country: 'IT', state: 'MI', city: 'Milano', zip: '20100' } },
      { id: 'store-coupon-validate', method: 'POST', path: '/store/coupons/validate', type: 'Guest', desc: 'Validate an ecommerce coupon before finalizing totals.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { code: 'WELCOME10', cartItems: [{ id: 1, quantity: 1, price: 10 }], subtotal: 10 } },
      { id: 'store-promo-validate', method: 'POST', path: '/store/promo-coupons/validate', type: 'Guest', desc: 'Validate a centralized promo coupon for ecommerce, salon, or xdelivery lines.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { module: 'ecommerce', coupon_code: 'WELCOME10', lines: [{ reference_type: 'product', reference_id: 1, quantity: 1, unit_price: 25 }] } },
      { id: 'store-promo-mine', method: 'GET', path: '/store/promo-coupons/mine', type: 'Member', auth: true, desc: 'Coupons currently available to the logged-in customer.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'store-orders-list', method: 'GET', path: '/store/orders', type: 'Guest', desc: 'Order history endpoint used by guest-email or logged-in customer flows.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'store-orders-create', method: 'POST', path: '/store/orders', type: 'Guest', desc: 'Create a new ecommerce order.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { checkoutData: { cartItems: [{ id: 1, quantity: 1, price: 10 }], addresses: [{ title: 'Home', email: 'user@example.com', address: 'Via Roma 1', city: 'Milan', zipCode: '20100', country: 'Italy', state: 'MI', phone: '123456' }], orderAmount: 10, paymentMethod: 'cod' } } },
      { id: 'store-payment-intent', method: 'POST', path: '/store/create-payment-intent', type: 'Guest', desc: 'Initialize a Stripe PaymentIntent for ecommerce checkout.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { amount: 29.99, currency: 'eur' } },
      { id: 'store-checkout-session', method: 'POST', path: '/store/create-checkout-session', type: 'Guest', desc: 'Create a Stripe Checkout Session for web redirect flows.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { cartItems: [{ id: 1, quantity: 1, price: 29.99 }], success_url: 'https://example.com/checkout/success', cancel_url: 'https://example.com/checkout/cancel' } },
      { id: 'store-checkout-verify', method: 'POST', path: '/store/create-checkout-session/verify', type: 'Guest', desc: 'Verify a Stripe Checkout Session after redirect completion.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { session_id: 'cs_test_123' } },
      { id: 'store-paypal-order', method: 'POST', path: '/store/paypal/order', type: 'Guest', desc: 'Create a PayPal order for ecommerce checkout.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { cartItems: [{ id: 1, quantity: 1, price: 29.99 }], currency: 'EUR' } },
      { id: 'store-paypal-capture', method: 'POST', path: '/store/paypal/capture', type: 'Guest', desc: 'Capture a PayPal order after buyer approval.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { order_id: 'PAYPAL_ORDER_ID' } },
    ],
  },
  {
    name: 'Loyalty & Referrals',
    icon: 'tabler-star',
    endpoints: [
      { id: 'loyalty-balance', method: 'GET', path: '/store/loyalty/balance', type: 'Member', auth: true, desc: 'Get current loyalty balance and tier.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'loyalty-rewards', method: 'GET', path: '/store/loyalty/rewards', type: 'Guest', desc: 'List available loyalty rewards.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'loyalty-history', method: 'GET', path: '/store/loyalty/history', type: 'Member', auth: true, desc: 'Points history for the logged-in customer.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'loyalty-redeem', method: 'POST', path: '/store/loyalty/redeem', type: 'Member', auth: true, desc: 'Redeem a loyalty reward from the app.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { reward_id: 1 } },
      { id: 'referrals-stats', method: 'GET', path: '/store/referrals/stats', type: 'Member', auth: true, desc: 'Referral stats and personal invite code.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'referrals-list', method: 'GET', path: '/store/referrals', type: 'Member', auth: true, desc: 'Detailed invited-friends list and earned rewards.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
    ],
  },
  {
    name: 'Salon Booking',
    icon: 'tabler-calendar-event',
    endpoints: [
      { id: 'salon-data', method: 'GET', path: '/store/salon/data', type: 'Guest', desc: 'Salon bootstrap payload: services, employees, branches, settings, and booking context.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'salon-availability', method: 'GET', path: '/store/salon/availability?branch_id={branchId}&date=2026-05-17', type: 'Guest', desc: 'Available slots for a branch and date.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'salon-branch-detail', method: 'GET', path: '/store/salon/branches/{id}', type: 'Guest', desc: 'Public branch detail.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'salon-portfolio', method: 'GET', path: '/store/salon/portfolio', type: 'Guest', desc: 'Public salon portfolio feed.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'salon-reviews', method: 'GET', path: '/store/salon/reviews', type: 'Guest', desc: 'Public salon reviews feed.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'salon-review-create', method: 'POST', path: '/store/salon/reviews', type: 'Member', auth: true, desc: 'Submit a salon review from the app.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { rating: 5, title: 'Excellent', comment: 'Very good experience' } },
      { id: 'salon-packages', method: 'GET', path: '/store/salon/packages', type: 'Guest', desc: 'Public package catalog for the salon module.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'salon-coupon-validate', method: 'POST', path: '/store/salon/coupons/validate', type: 'Guest', desc: 'Validate a salon coupon against the current booking payload.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { coupon_code: 'WELCOME10', booking_payload: { branch_id: 1, service_ids: [1, 2], subtotal: 80 } } },
      { id: 'salon-book', method: 'POST', path: '/store/salon/book', type: 'Member', auth: true, desc: 'Create a salon booking for the authenticated customer.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { branch_id: 1, employee_id: 2, service_ids: [1], booking_date: '2026-05-17', booking_time: '10:30', payment_method: 'pay_in_store' } },
      { id: 'salon-my-bookings', method: 'GET', path: '/store/salon/my-bookings', type: 'Member', auth: true, desc: 'Authenticated customer bookings list.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'salon-my-booking-detail', method: 'GET', path: '/store/salon/my-bookings/{id}', type: 'Member', auth: true, desc: 'Single booking detail for the authenticated customer.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'salon-my-booking-cancel', method: 'POST', path: '/store/salon/my-bookings/{id}/cancel', type: 'Member', auth: true, desc: 'Cancel an existing booking when runtime rules allow it.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { reason: 'Schedule changed' } },
      { id: 'salon-my-packages', method: 'GET', path: '/store/salon/my-packages', type: 'Member', auth: true, desc: 'Package inventory and remaining sessions for the logged-in customer.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'salon-package-purchase', method: 'POST', path: '/store/salon/packages/{id}/purchase', type: 'Member', auth: true, desc: 'Buy a salon package directly.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { payment_method: 'stripe', return_url: 'https://example.com/salon/packages' } },
      { id: 'salon-package-stripe-intent', method: 'POST', path: '/store/salon/packages/{id}/stripe/payment-intent', type: 'Member', auth: true, desc: 'Create a Stripe PaymentIntent for salon package checkout.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { currency: 'eur' } },
      { id: 'salon-package-stripe-intent-finalize', method: 'POST', path: '/store/salon/packages/stripe/payment-intent/finalize', type: 'Member', auth: true, desc: 'Finalize a salon package PaymentIntent after successful confirmation.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { payment_intent_id: 'pi_123', package_id: 7 } },
      { id: 'salon-package-stripe-session', method: 'POST', path: '/store/salon/packages/{id}/stripe/checkout-session', type: 'Member', auth: true, desc: 'Create a Stripe Checkout Session for salon package checkout.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { success_url: 'https://example.com/salon/package-success', cancel_url: 'https://example.com/salon/package-cancel' } },
      { id: 'salon-package-stripe-session-verify', method: 'POST', path: '/store/salon/packages/stripe/checkout-session/verify', type: 'Member', auth: true, desc: 'Verify a salon package Stripe Checkout Session after redirect.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { session_id: 'cs_test_package_123' } },
      { id: 'salon-package-paypal-order', method: 'POST', path: '/store/salon/packages/{id}/paypal/order', type: 'Member', auth: true, desc: 'Create a PayPal order for salon package checkout.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { currency: 'EUR' } },
      { id: 'salon-package-paypal-capture', method: 'POST', path: '/store/salon/packages/paypal/capture', type: 'Member', auth: true, desc: 'Capture an approved PayPal payment for a salon package.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { order_id: 'PAYPAL_ORDER_ID' } },
      { id: 'salon-booking-stripe-intent', method: 'POST', path: '/store/salon/stripe/payment-intent', type: 'Member', auth: true, desc: 'Create a Stripe PaymentIntent for a salon booking.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { branch_id: 1, service_ids: [1], booking_date: '2026-05-17', booking_time: '10:30', currency: 'eur' } },
      { id: 'salon-booking-stripe-intent-finalize', method: 'POST', path: '/store/salon/stripe/payment-intent/finalize', type: 'Member', auth: true, desc: 'Finalize a salon booking after a successful Stripe PaymentIntent.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { payment_intent_id: 'pi_123', branch_id: 1, service_ids: [1] } },
      { id: 'salon-booking-stripe-session', method: 'POST', path: '/store/salon/stripe/checkout-session', type: 'Member', auth: true, desc: 'Create a Stripe Checkout Session for salon booking flows.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { branch_id: 1, service_ids: [1], booking_date: '2026-05-17', booking_time: '10:30', success_url: 'https://example.com/salon/success', cancel_url: 'https://example.com/salon/cancel' } },
      { id: 'salon-booking-stripe-session-verify', method: 'POST', path: '/store/salon/stripe/checkout-session/verify', type: 'Member', auth: true, desc: 'Verify a salon Stripe Checkout Session after redirect completion.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { session_id: 'cs_test_123' } },
      { id: 'salon-booking-paypal-order', method: 'POST', path: '/store/salon/paypal/order', type: 'Member', auth: true, desc: 'Create a PayPal order for a salon booking.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { branch_id: 1, service_ids: [1], booking_date: '2026-05-17', booking_time: '10:30', currency: 'EUR' } },
      { id: 'salon-booking-paypal-capture', method: 'POST', path: '/store/salon/paypal/capture', type: 'Member', auth: true, desc: 'Capture an approved PayPal payment for a salon booking.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { order_id: 'PAYPAL_ORDER_ID' } },
      { id: 'salon-waitlist-create', method: 'POST', path: '/store/salon/waitlist', type: 'Member', auth: true, desc: 'Join the salon waitlist from the app.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { branch_id: 1, service_ids: [2], desired_date: '2026-05-17' } },
    ],
  },
  {
    name: 'Salon Staff In-App',
    icon: 'tabler-scissors',
    endpoints: [
      { id: 'salon-staff-me', method: 'GET', path: '/store/salon/staff/me', type: 'Staff', auth: true, desc: 'Resolve the logged-in salon collaborator profile.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'salon-staff-bookings', method: 'GET', path: '/store/salon/staff/bookings', type: 'Staff', auth: true, desc: 'Booking list for the collaborator in-app workspace.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'salon-staff-booking-update', method: 'PUT', path: '/store/salon/staff/bookings/{id}', type: 'Staff', auth: true, desc: 'Update a booking from the collaborator app.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { status: 'confirmed', notes: 'Customer called to confirm' } },
      { id: 'salon-staff-services', method: 'GET', path: '/store/salon/staff/services', type: 'Staff', auth: true, desc: 'List services manageable by the collaborator.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'salon-staff-service-create', method: 'POST', path: '/store/salon/staff/services', type: 'Staff', auth: true, desc: 'Create a service from the collaborator app when allowed.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { name: 'Cut & Style', duration_minutes: 45, price: 35 } },
      { id: 'salon-staff-service-update', method: 'PUT', path: '/store/salon/staff/services/{id}', type: 'Staff', auth: true, desc: 'Update a service from the collaborator app.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { name: 'Cut & Style', duration_minutes: 50, price: 38 } },
      { id: 'salon-staff-service-delete', method: 'DELETE', path: '/store/salon/staff/services/{id}', type: 'Staff', auth: true, desc: 'Delete a collaborator-manageable service.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'salon-staff-schedule', method: 'GET', path: '/store/salon/staff/schedule', type: 'Staff', auth: true, desc: 'Get the weekly schedule for the logged-in collaborator.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'salon-staff-schedule-update', method: 'POST', path: '/store/salon/staff/schedule', type: 'Staff', auth: true, desc: 'Update weekly working slots for the collaborator.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { monday: [{ start: '09:00', end: '18:00' }] } },
      { id: 'salon-staff-timeoff', method: 'GET', path: '/store/salon/staff/time-off', type: 'Staff', auth: true, desc: 'List upcoming time-off entries.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'salon-staff-timeoff-create', method: 'POST', path: '/store/salon/staff/time-off', type: 'Staff', auth: true, desc: 'Create a time-off entry from the collaborator app.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { start_at: '2026-05-17 09:00:00', end_at: '2026-05-17 13:00:00', reason: 'Medical appointment' } },
      { id: 'salon-staff-timeoff-delete', method: 'DELETE', path: '/store/salon/staff/time-off/{timeOffId}', type: 'Staff', auth: true, desc: 'Delete a time-off entry.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'salon-staff-report-sales', method: 'GET', path: '/store/salon/staff/reports/sales', type: 'Staff', auth: true, desc: 'Sales report used by the collaborator reporting screen.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
    ],
  },
  {
    name: 'XDelivery Store & Table QR',
    icon: 'tabler-motorbike',
    endpoints: [
      { id: 'xdelivery-locations', method: 'GET', path: '/store/xdelivery/locations', type: 'Guest', desc: 'Locations list for xdelivery ordering and reservations.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'xdelivery-settings', method: 'GET', path: '/store/xdelivery/settings', type: 'Guest', desc: 'Public xdelivery settings and service availability.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'xdelivery-menu', method: 'GET', path: '/store/xdelivery/menu', type: 'Guest', desc: 'Menu payload with categories, variants, and addon categories.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'xdelivery-availability', method: 'GET', path: '/store/xdelivery/availability', type: 'Guest', desc: 'Available delivery or pickup windows.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'xdelivery-quote', method: 'POST', path: '/store/xdelivery/quote', type: 'Guest', desc: 'Calculate delivery quote and totals before checkout.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { location_id: 1, address: { lat: 45.46, lng: 9.18 }, items: [{ id: 1, quantity: 2 }] } },
      { id: 'xdelivery-geocode', method: 'GET', path: '/store/xdelivery/geocode/reverse?lat=45.46&lng=9.18', type: 'Guest', desc: 'Reverse geocode helper for delivery addresses.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'xdelivery-coupon-validate', method: 'POST', path: '/store/xdelivery/coupons/validate', type: 'Guest', desc: 'Validate an xdelivery coupon.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { code: 'WELCOME10', subtotal: 25, location_id: 1 } },
      { id: 'xdelivery-stripe-intent', method: 'POST', path: '/store/xdelivery/stripe/payment-intent', type: 'Guest', desc: 'Create a Stripe PaymentIntent for xdelivery checkout.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { amount: 2599, currency: 'EUR' } },
      { id: 'xdelivery-stripe-session', method: 'POST', path: '/store/xdelivery/stripe/checkout-session', type: 'Guest', desc: 'Create a Stripe Checkout Session for xdelivery checkout.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { success_url: 'https://example.com/xdelivery/success', cancel_url: 'https://example.com/xdelivery/cancel' } },
      { id: 'xdelivery-paypal-order', method: 'POST', path: '/store/xdelivery/paypal/order', type: 'Guest', desc: 'Create a PayPal order for xdelivery checkout.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { currency: 'EUR', items: [{ id: 1, quantity: 1 }] } },
      { id: 'xdelivery-paypal-capture', method: 'POST', path: '/store/xdelivery/paypal/capture', type: 'Guest', desc: 'Capture an approved PayPal payment for xdelivery.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { order_id: 'PAYPAL_ORDER_ID' } },
      { id: 'xdelivery-orders-create', method: 'POST', path: '/store/xdelivery/orders', type: 'Guest', desc: 'Create an xdelivery order.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { location_id: 1, type: 'delivery', customer: { full_name: 'Mario Rossi', phone: '+39 333 1234567' }, items: [{ id: 1, quantity: 2 }] } },
      { id: 'xdelivery-orders-list', method: 'GET', path: '/store/xdelivery/orders', type: 'Guest', desc: 'Guest-or-authenticated xdelivery order history endpoint.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'xdelivery-order-detail', method: 'GET', path: '/store/xdelivery/orders/{order}?token={guestToken}', type: 'Guest', desc: 'Order tracking detail. Logged-in users can open their own order directly; guest tracking requires the opaque order token.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'xdelivery-reservation-availability', method: 'GET', path: '/store/xdelivery/reservations/availability', type: 'Guest', desc: 'Available reservation slots for the xdelivery/table module.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'xdelivery-reservation-create', method: 'POST', path: '/store/xdelivery/reservations', type: 'Guest', desc: 'Create a reservation from the app.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { location_id: 1, date: '2026-05-17', time: '20:30', guests: 2, full_name: 'Mario Rossi' } },
      { id: 'xdelivery-reservation-mine', method: 'GET', path: '/store/xdelivery/reservations/mine', type: 'Guest', desc: 'Reservations list for the current guest or logged-in customer.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'xdelivery-table-resolve', method: 'GET', path: '/store/xdelivery/table/{qrToken}', type: 'Guest', desc: 'Resolve a table QR code into the current table context.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'xdelivery-table-call-waiter', method: 'POST', path: '/store/xdelivery/table/{qrToken}/call-waiter', type: 'Guest', desc: 'Call a waiter from the table QR experience.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'xdelivery-table-cart', method: 'GET', path: '/store/xdelivery/table/{qrToken}/cart', type: 'Guest', desc: 'Read the shared server-side cart for the table.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'xdelivery-table-cart-add', method: 'POST', path: '/store/xdelivery/table/{qrToken}/cart/items', type: 'Guest', desc: 'Add an item to the shared table cart.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { menu_item_id: 10, quantity: 1, notes: 'No onions' } },
      { id: 'xdelivery-table-cart-update', method: 'PUT', path: '/store/xdelivery/table/{qrToken}/cart/items/{lineId}', type: 'Guest', desc: 'Update quantity or notes for a table cart line.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { quantity: 2, notes: 'Extra sauce' } },
      { id: 'xdelivery-table-cart-delete', method: 'DELETE', path: '/store/xdelivery/table/{qrToken}/cart/items/{lineId}', type: 'Guest', desc: 'Remove an item from the shared table cart.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'xdelivery-table-order', method: 'POST', path: '/store/xdelivery/table/{qrToken}/order', type: 'Guest', desc: 'Place a table order from the QR cart.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { payment_method: 'pay_at_table', customer_name: 'Mario Rossi' } },
    ],
  },
  {
    name: 'XDelivery Staff In-App',
    icon: 'tabler-chef-hat',
    endpoints: [
      { id: 'xdelivery-staff-me', method: 'GET', path: '/store/xdelivery/staff/me', type: 'Staff', auth: true, desc: 'Resolve the logged-in xdelivery staff profile.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'xdelivery-staff-orders', method: 'GET', path: '/store/xdelivery/staff/orders', type: 'Staff', auth: true, desc: 'Order queue for xdelivery staff.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'xdelivery-staff-ticket', method: 'GET', path: '/store/xdelivery/staff/orders/{order}/ticket', type: 'Staff', auth: true, desc: 'Printable ticket payload for a specific order.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'xdelivery-staff-print', method: 'POST', path: '/store/xdelivery/staff/orders/{order}/print', type: 'Staff', auth: true, desc: 'Queue a staff-side print action for an order.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'xdelivery-staff-accept', method: 'POST', path: '/store/xdelivery/staff/orders/{order}/accept', type: 'Staff', auth: true, desc: 'Accept an incoming order from the staff app.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'xdelivery-staff-decline', method: 'POST', path: '/store/xdelivery/staff/orders/{order}/decline', type: 'Staff', auth: true, desc: 'Decline an order from the staff app.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'xdelivery-staff-status', method: 'PUT', path: '/store/xdelivery/staff/orders/{order}/status', type: 'Staff', auth: true, desc: 'Advance order status from the staff app.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { status: 'ready' } },
      { id: 'xdelivery-staff-categories', method: 'GET', path: '/store/xdelivery/staff/categories', type: 'Staff', auth: true, desc: 'Category list manageable from the staff app.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'xdelivery-staff-category-create', method: 'POST', path: '/store/xdelivery/staff/categories', type: 'Staff', auth: true, desc: 'Create a category from the staff app.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { name: 'Drinks', is_active: true } },
      { id: 'xdelivery-staff-category-update', method: 'PUT', path: '/store/xdelivery/staff/categories/{category}', type: 'Staff', auth: true, desc: 'Update a category from the staff app.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { name: 'Cold Drinks', is_active: true } },
      { id: 'xdelivery-staff-menu-items', method: 'GET', path: '/store/xdelivery/staff/menu-items', type: 'Staff', auth: true, desc: 'List menu items manageable from the staff app.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'xdelivery-staff-menu-item-create', method: 'POST', path: '/store/xdelivery/staff/menu-items', type: 'Staff', auth: true, desc: 'Create a menu item from the staff app.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { name: 'Burger', price: 12.5, category_id: 1 } },
      { id: 'xdelivery-staff-menu-item-update', method: 'PUT', path: '/store/xdelivery/staff/menu-items/{menuItem}', type: 'Staff', auth: true, desc: 'Update a menu item from the staff app.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { name: 'Double Burger', price: 14.5 } },
      { id: 'xdelivery-staff-addon-categories', method: 'GET', path: '/store/xdelivery/staff/addon-categories', type: 'Staff', auth: true, desc: 'List addon categories for the staff app.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'xdelivery-staff-addon-category-create', method: 'POST', path: '/store/xdelivery/staff/addon-categories', type: 'Staff', auth: true, desc: 'Create an addon category from the staff app.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { name: 'Extras' } },
      { id: 'xdelivery-staff-addon-category-update', method: 'PUT', path: '/store/xdelivery/staff/addon-categories/{addonCategory}', type: 'Staff', auth: true, desc: 'Update an addon category from the staff app.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { name: 'Premium Extras' } },
      { id: 'xdelivery-staff-addon-save', method: 'POST', path: '/store/xdelivery/staff/addon-categories/{addonCategory}/addons', type: 'Staff', auth: true, desc: 'Persist addon options for a staff-managed addon category.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { addons: [{ name: 'Extra cheese', price: 1.5 }] } },
      { id: 'xdelivery-staff-reservations', method: 'GET', path: '/store/xdelivery/staff/reservations', type: 'Staff', auth: true, desc: 'Reservation queue for xdelivery/table staff.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'xdelivery-staff-reservation-status', method: 'PUT', path: '/store/xdelivery/staff/reservations/{reservation}/status', type: 'Staff', auth: true, desc: 'Update reservation status from the staff app.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { status: 'confirmed' } },
      { id: 'xdelivery-staff-tables', method: 'GET', path: '/store/xdelivery/staff/tables', type: 'Staff', auth: true, desc: 'Table list used by the reservation management screen.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'xdelivery-staff-report-sales', method: 'GET', path: '/store/xdelivery/staff/reports/sales', type: 'Staff', auth: true, desc: 'Sales report used by xdelivery staff roles inside the app.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
    ],
  },
  {
    name: 'Community Hub',
    icon: 'tabler-message-circle',
    endpoints: [
      { id: 'community-rooms', method: 'GET', path: '/store/community-hub/rooms', type: 'Guest', desc: 'Public room listing for the community module.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'community-room-detail', method: 'GET', path: '/store/community-hub/rooms/{thread}', type: 'Guest', desc: 'Public detail for a community room.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'community-realtime-config', method: 'GET', path: '/store/community-hub/realtime-config', type: 'Member', auth: true, desc: 'Realtime provider configuration used by the in-app chat client.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'community-my-rooms', method: 'GET', path: '/store/community-hub/my-rooms', type: 'Member', auth: true, desc: 'List joined rooms for the authenticated customer.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'community-my-room-detail', method: 'GET', path: '/store/community-hub/my-rooms/{thread}', type: 'Member', auth: true, desc: 'Load one joined room with thread messages.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'community-invitations', method: 'GET', path: '/store/community-hub/invitations', type: 'Member', auth: true, desc: 'Pending room invitations for the current user.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'community-direct-threads', method: 'GET', path: '/store/community-hub/direct-threads', type: 'Member', auth: true, desc: 'Direct-message thread list.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'community-direct-thread-create', method: 'POST', path: '/store/community-hub/direct-threads', type: 'Member', auth: true, desc: 'Open a new direct-message thread.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { participant_id: 15 } },
      { id: 'community-direct-thread-detail', method: 'GET', path: '/store/community-hub/direct-threads/{thread}', type: 'Member', auth: true, desc: 'Load a direct-message thread.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'community-direct-thread-read', method: 'POST', path: '/store/community-hub/direct-threads/{thread}/read', type: 'Member', auth: true, desc: 'Mark a direct thread as read.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'community-direct-thread-message', method: 'POST', path: '/store/community-hub/direct-threads/{thread}/messages', type: 'Member', auth: true, desc: 'Send a message into a direct thread.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { body: 'Hello there!' } },
      { id: 'community-room-join', method: 'POST', path: '/store/community-hub/rooms/{thread}/join', type: 'Member', auth: true, desc: 'Join a community room.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'community-room-leave', method: 'POST', path: '/store/community-hub/rooms/{thread}/leave', type: 'Member', auth: true, desc: 'Leave a joined room.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'community-room-read', method: 'POST', path: '/store/community-hub/rooms/{thread}/read', type: 'Member', auth: true, desc: 'Mark a room thread as read.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'community-room-message', method: 'POST', path: '/store/community-hub/rooms/{thread}/messages', type: 'Member', auth: true, desc: 'Send a message into a joined room.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { body: 'Hello everyone!' } },
      { id: 'community-thread-preferences', method: 'PATCH', path: '/store/community-hub/threads/{thread}/preferences', type: 'Member', auth: true, desc: 'Update preferences such as mute state for a thread.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { notifications_enabled: false } },
    ],
  },
  {
    name: 'Attendance',
    icon: 'tabler-clock-hour-4',
    endpoints: [
      { id: 'attendance-me', method: 'GET', path: '/store/attendance/me', type: 'Staff', auth: true, desc: 'Current clock-in status for the authenticated staff user.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'attendance-history', method: 'GET', path: '/store/attendance/history', type: 'Staff', auth: true, desc: 'Attendance history for the current staff user.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'attendance-clockin', method: 'POST', path: '/store/attendance/clock-in', type: 'Staff', auth: true, desc: 'Clock in from the attendance app flow.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'attendance-clockout', method: 'POST', path: '/store/attendance/clock-out', type: 'Staff', auth: true, desc: 'Clock out from the attendance app flow.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'attendance-break-start', method: 'POST', path: '/store/attendance/break-start', type: 'Staff', auth: true, desc: 'Start a break.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'attendance-break-end', method: 'POST', path: '/store/attendance/break-end', type: 'Staff', auth: true, desc: 'End a break.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
    ],
  },
  {
    name: 'Nutrition & Wellness',
    icon: 'tabler-apple',
    endpoints: [
      { id: 'nutrition-biometrics-save', method: 'POST', path: '/nutritionist/biometrics', type: 'Member', auth: true, desc: 'Save biometric data such as age, weight, and height.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { age: 30, weight: 80, height: 180, gender: 'male' } },
      { id: 'nutrition-biometrics', method: 'GET', path: '/nutritionist/biometrics', type: 'Member', auth: true, desc: 'Read saved biometrics for the logged-in customer.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'nutrition-diet', method: 'GET', path: '/nutritionist/diet/{biometrics_id}', type: 'Member', auth: true, desc: 'Generate or fetch a personalized diet suggestion.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'nutrition-plan-accept', method: 'POST', path: '/nutritionist/plan/accept', type: 'Member', auth: true, desc: 'Accept and save a suggested diet plan.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { biometrics_id: 1, diet_plan_id: 1 } },
      { id: 'nutrition-plan-consult', method: 'POST', path: '/nutritionist/plan/consult', type: 'Member', auth: true, desc: 'Request a consultation after reviewing the suggested plan.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { biometrics_id: 1, message: 'I need a lower-carb variant.' } },
      { id: 'nutrition-user-diets', method: 'GET', path: '/nutritionist/user-diets', type: 'Member', auth: true, desc: 'Saved diet history for the current customer.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'nutrition-user-diet-delete', method: 'DELETE', path: '/nutritionist/user-diets/{id}', type: 'Member', auth: true, desc: 'Delete a saved diet plan from the customer history.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
    ],
  },
  {
    name: 'Forms, Content & Campaigns',
    icon: 'tabler-layout-list',
    endpoints: [
      { id: 'forms-list', method: 'GET', path: '/store/forms', type: 'Guest', desc: 'List active tenant forms.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'forms-detail', method: 'GET', path: '/store/forms/{id}', type: 'Guest', desc: 'Get the field structure of a specific form.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'forms-submit', method: 'POST', path: '/store/forms/{id}/submit', type: 'Member', auth: true, desc: 'Submit form answers from the app.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { data: { field_1: 'Value', field_2: 'Other' } } },
      { id: 'backoffice-form-submissions-list', method: 'GET', path: '/apps/form-submissions', type: 'Server', auth: true, desc: 'Read submitted form answers across all forms (tenant token, not an app user token), optionally filtered by form_id/status/from/to. Each submission includes an attachments array with directly downloadable URLs for every uploaded file, plus signature/pdf URLs.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'backoffice-form-submission-detail', method: 'GET', path: '/apps/form-submissions/{id}', type: 'Server', auth: true, desc: 'Read one form submission, including directly downloadable URLs for every file attachment.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'backoffice-form-field-options', method: 'PUT', path: '/apps/forms/{form}/fields/{name}/options', type: 'Server', auth: true, desc: 'Update the choice list of a select/radio/multi_select field, identified by its technical name - does not touch any other field or any other attribute of the same field. Rejects field types that do not use options (422).', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { options: ['Cioccolato', 'Panna', 'Nocciola'] } },
      { id: 'custom-html-public', method: 'GET', path: '/store/custom-html', type: 'Guest', desc: 'Public payload for the custom HTML module.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'custom-page-public', method: 'GET', path: '/store/custom-page', type: 'Guest', desc: 'Public payload for the default custom page.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'custom-page-slug', method: 'GET', path: '/store/custom-pages/{slug}', type: 'Guest', desc: 'Public payload for a specific custom page slug.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'galleries-list', method: 'GET', path: '/store/galleries', type: 'Guest', desc: 'List the active galleries of the app with cover, layout and item count.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'gallery-detail', method: 'GET', path: '/store/galleries/{slug}', type: 'Guest', desc: 'Public payload of one gallery: layout, complete layout options and its visible photos and videos.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'funnels-list', method: 'GET', path: '/store/funnels', type: 'Guest', desc: 'List the journeys (funnels) shown in the app with the number of steps and, when a customer Bearer token is sent, how many they have completed.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'funnel-detail', method: 'GET', path: '/store/funnels/{slug}', type: 'Guest', desc: 'One journey with its steps the customer can perform, their optional call-to-action and, with a customer Bearer token, which steps are already completed.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'webviews-list', method: 'GET', path: '/store/webviews', type: 'Guest', desc: 'Public webview listing consumed by the app.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'webview-group-detail', method: 'GET', path: '/store/webview-groups/{slug}', type: 'Guest', desc: 'Public webview group detail by slug.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'popup-active', method: 'GET', path: '/store/popup/active', type: 'Guest', desc: 'Resolve the active popup campaign for the current app session.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'popup-impression', method: 'POST', path: '/store/popup/{campaign}/impression', type: 'Member', auth: true, desc: 'Record a popup impression for analytics and frequency control.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
    ],
  },
  {
    name: 'Scratch Win & Geo',
    icon: 'tabler-map-pin',
    endpoints: [
      { id: 'scratch-campaign', method: 'GET', path: '/store/scratch-win/campaign', type: 'Guest', desc: 'Current scratch campaign payload.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'scratch-me', method: 'GET', path: '/store/scratch-win/me', type: 'Guest', desc: 'Current player status for scratch win.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'scratch-play', method: 'POST', path: '/store/scratch-win/play', type: 'Guest', desc: 'Execute a scratch attempt.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'scratch-history', method: 'GET', path: '/store/scratch-win/history', type: 'Guest', desc: 'Scratch play history for the current player.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'scratch-claim', method: 'POST', path: '/store/scratch-win/plays/{play}/claim', type: 'Guest', desc: 'Claim a reward from a previous scratch play.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'geo-countries', method: 'GET', path: '/store/geo/countries', type: 'Guest', desc: 'Enabled countries list.', headers: [] },
      { id: 'geo-states', method: 'GET', path: '/store/geo/countries/{id}/states', type: 'Guest', desc: 'States or provinces for a country.', headers: [] },
      { id: 'geo-cities', method: 'GET', path: '/store/geo/countries/{id}/states/{stateId}/cities', type: 'Guest', desc: 'Cities available for the selected country and state.', headers: [] },
    ],
  },
  {
    name: 'Ride Hailing',
    icon: 'tabler-car',
    endpoints: [
      { id: 'ride-hailing-driver-application-create', method: 'POST', path: '/store/ride-hailing/driver-applications', type: 'Member', auth: true, desc: 'Submit a driver application for the authenticated app user.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { vehicle_make: 'Fiat', vehicle_model: 'Panda', vehicle_plate: 'AB123CD' } },
      { id: 'ride-hailing-driver-application-me', method: 'GET', path: '/store/ride-hailing/driver-applications/me', type: 'Member', auth: true, desc: "Read the authenticated user's own driver application status (pending/approved/rejected).", headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'ride-hailing-driver-me', method: 'GET', path: '/store/ride-hailing/driver/me', type: 'Member', auth: true, desc: "Read the authenticated user's own driver profile (only present once approved).", headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'ride-hailing-driver-status', method: 'POST', path: '/store/ride-hailing/driver/status', type: 'Member', auth: true, desc: 'Toggle the approved driver online/offline.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { status: 'online' } },
      { id: 'ride-hailing-driver-location', method: 'POST', path: '/store/ride-hailing/driver/location', type: 'Member', auth: true, desc: 'Ping the current GPS position while online (used for matching and rider-facing driver display).', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { lat: 45.4642, lng: 9.19, heading: 90 } },
      { id: 'ride-hailing-rides-estimate', method: 'POST', path: '/store/ride-hailing/rides/estimate', type: 'Member', auth: true, desc: 'Server-side fare/distance/duration estimate for a pickup-dropoff pair, no ride is created.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { pickup_lat: 45.4642, pickup_lng: 9.19, dropoff_lat: 45.4781, dropoff_lng: 9.2264 } },
      { id: 'ride-hailing-rides-payment-intent', method: 'POST', path: '/store/ride-hailing/rides/payment-intent', type: 'Member', auth: true, desc: 'Create a Stripe PaymentIntent for the estimated fare (requires Stripe configured for this app in Settings > Payment Gateways).', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { pickup_lat: 45.4642, pickup_lng: 9.19, dropoff_lat: 45.4781, dropoff_lng: 9.2264 } },
      { id: 'ride-hailing-rides-create', method: 'POST', path: '/store/ride-hailing/rides', type: 'Member', auth: true, desc: 'Request a ride. payment_method cash is accepted immediately; stripe requires a payment_intent_id already verified server-side as succeeded.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { pickup_lat: 45.4642, pickup_lng: 9.19, pickup_address: 'Piazza Duomo, Milano', dropoff_lat: 45.4781, dropoff_lng: 9.2264, dropoff_address: 'Stazione Centrale, Milano', payment_method: 'cash' } },
      { id: 'ride-hailing-rides-active', method: 'GET', path: '/store/ride-hailing/rides/active?role=rider', type: 'Member', auth: true, desc: "Current non-terminal ride for the authenticated user. role=rider (default) or role=driver - always explicit, never falls back between the two.", headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'ride-hailing-rides-show', method: 'GET', path: '/store/ride-hailing/rides/{id}', type: 'Member', auth: true, desc: 'Ride detail, including rider and assigned driver (name/contact/vehicle). Accessible by the rider, the assigned driver, or a tenant backend user.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'ride-hailing-rides-cancel', method: 'POST', path: '/store/ride-hailing/rides/{id}/cancel', type: 'Member', auth: true, desc: 'Cancel a ride not yet in progress. Automatically refunds via Stripe if it was already paid by card.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { reason: 'Changed my mind' } },
      { id: 'ride-hailing-rides-driver-arrived', method: 'POST', path: '/store/ride-hailing/rides/{id}/driver-arrived', type: 'Member', auth: true, desc: 'Driver-only: mark arrival at the pickup point (driver_assigned -> driver_arriving).', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'ride-hailing-rides-start', method: 'POST', path: '/store/ride-hailing/rides/{id}/start', type: 'Member', auth: true, desc: 'Driver-only: start the ride (driver_arriving -> in_progress).', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'ride-hailing-rides-complete', method: 'POST', path: '/store/ride-hailing/rides/{id}/complete', type: 'Member', auth: true, desc: 'Driver-only: complete the ride. Final fare is recalculated server-side, cash payments are marked collected automatically, the driver is freed back to online.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'ride-hailing-rides-rate', method: 'POST', path: '/store/ride-hailing/rides/{id}/rate', type: 'Member', auth: true, desc: 'Rider-only: rate a completed ride once, updating the driver rating average.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { rating: 5, comment: 'Great ride!' } },
    ],
  },
  {
    name: 'ProFinder',
    icon: 'tabler-map-search',
    endpoints: [
      { id: 'profinder-categories', method: 'GET', path: '/store/profinder/categories', type: 'Guest', desc: 'Active service categories shown as icons on the ProFinder home.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'profinder-providers-search', method: 'GET', path: '/store/profinder/providers?category_id={categoryId}&lat={lat}&lng={lng}&radius={radiusKm}&q={query}&min_rating={minRating}', type: 'Guest', desc: 'Search active providers by category, keyword, minimum rating and proximity (radius in km around lat/lng). Falls back to a Google-imported result when internal providers are scarce.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'profinder-provider-show', method: 'GET', path: '/store/profinder/providers/{id}', type: 'Guest', desc: 'Public showcase of a single provider: gallery, locations, services, rating.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'profinder-provider-register-contact', method: 'POST', path: '/store/profinder/providers/{id}/register-contact', type: 'Guest', desc: 'Increment the provider contact counter shown in their stats (called when a customer taps call/website/directions).', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'profinder-provider-reviews', method: 'GET', path: '/store/profinder/providers/{providerId}/reviews', type: 'Guest', desc: 'Approved reviews for a provider.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'profinder-provider-me', method: 'GET', path: '/store/profinder/providers/me', type: 'Member', auth: true, desc: 'Load the showcase owned by the authenticated provider, including view/contact stats.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'profinder-provider-update', method: 'PUT', path: '/store/profinder/providers/{id}', type: 'Member', auth: true, desc: 'Update the authenticated provider\'s own showcase (name, description, contacts, gallery, bio sections).', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { name: 'Idraulico Rossi', description: 'Interventi urgenti 24/7', phone: '+39 333 1234567', website: 'https://idraulicorossi.it', address: 'Via Roma 1, Milano' } },
      { id: 'profinder-applications-store', method: 'POST', path: '/store/profinder/applications', type: 'Member', auth: true, desc: 'Apply to become a provider (one pending/approved application per user). Rate limited.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { business_name: 'Idraulico Rossi', address: 'Via Roma 1, Milano', phone: '+39 333 1234567', website: 'https://idraulicorossi.it', category_ids: [1] } },
      { id: 'profinder-applications-me', method: 'GET', path: '/store/profinder/applications/me', type: 'Member', auth: true, desc: 'Status of the authenticated user\'s own provider application (pending/approved/rejected).', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'profinder-favorites-list', method: 'GET', path: '/store/profinder/favorites', type: 'Member', auth: true, desc: 'List providers saved as favorites by the authenticated customer.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'profinder-favorites-store', method: 'POST', path: '/store/profinder/favorites/{providerId}', type: 'Member', auth: true, desc: 'Save a provider as favorite. Idempotent.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'profinder-favorites-destroy', method: 'DELETE', path: '/store/profinder/favorites/{providerId}', type: 'Member', auth: true, desc: 'Remove a provider from favorites. Idempotent.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'profinder-review-me', method: 'GET', path: '/store/profinder/providers/{providerId}/reviews/me', type: 'Member', auth: true, desc: 'The authenticated customer\'s own review for a provider, whatever its moderation status.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'profinder-review-store', method: 'POST', path: '/store/profinder/providers/{providerId}/reviews', type: 'Member', auth: true, desc: 'Submit a 1-5 star review with an optional comment (one per user per provider). Stays pending until the tenant approves it. Rate limited.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { rating: 5, comment: 'Molto professionale e puntuale.' } },
      { id: 'profinder-thread-open', method: 'POST', path: '/store/profinder/providers/{providerId}/thread', type: 'Member', auth: true, desc: 'Open (or reuse) a chat thread with a provider. Fails if the provider has no linked user account.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'profinder-threads-list', method: 'GET', path: '/store/profinder/threads', type: 'Member', auth: true, desc: 'List the authenticated user\'s ProFinder chat threads (as customer or as provider).', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'profinder-thread-messages-list', method: 'GET', path: '/store/profinder/threads/{threadId}/messages', type: 'Member', auth: true, desc: 'List messages of a thread the authenticated user participates in.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'profinder-thread-messages-send', method: 'POST', path: '/store/profinder/threads/{threadId}/messages', type: 'Member', auth: true, desc: 'Send a text message in a thread the authenticated user participates in.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { body: 'Buongiorno, e\' disponibile domani mattina?' } },
    ],
  },
  {
    name: 'Book Catalog',
    icon: 'tabler-books',
    endpoints: [
      { id: 'book-catalog-categories', method: 'GET', path: '/store/book-catalog/categories', type: 'Guest', desc: 'Active book categories shown as icons on the catalog home.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'book-catalog-books-list', method: 'GET', path: '/store/book-catalog/books?category_id={categoryId}&q={query}&page={page}', type: 'Guest', desc: 'Paginated list of published books, filterable by category and free-text search on title/author/tags.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'book-catalog-book-show', method: 'GET', path: '/store/book-catalog/books/{id}', type: 'Guest', desc: 'Book detail: cover, description, tags, page count, rating. Increments the view counter.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'book-catalog-book-reviews', method: 'GET', path: '/store/book-catalog/books/{bookId}/reviews', type: 'Guest', desc: 'Approved reviews for a book.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'book-catalog-settings', method: 'GET', path: '/store/book-catalog/settings', type: 'Guest', desc: 'Whether the tenant currently requires login to read PDFs, and the configured in-app page background.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'book-catalog-public-file', method: 'GET', path: '/store/book-catalog/books/{id}/public-file', type: 'Guest', desc: 'Streams the PDF WITHOUT login, but only if the tenant turned off "Require login to view PDFs" in Settings - otherwise this responds 401 and the authenticated /file endpoint below is required instead. Rate limited.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'book-catalog-file', method: 'GET', path: '/store/book-catalog/books/{id}/file', type: 'Member', auth: true, desc: 'Streams the PDF for a logged-in customer. Never a public static URL by design.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'book-catalog-progress-show', method: 'GET', path: '/store/book-catalog/books/{id}/progress', type: 'Member', auth: true, desc: 'The authenticated customer\'s reading progress for a book (last page, percent, completed_at).', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'book-catalog-progress-update', method: 'PUT', path: '/store/book-catalog/books/{id}/progress', type: 'Member', auth: true, desc: 'Save the last page reached. Percent is computed server-side from page_count when known; completed_at is set automatically on the last page.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { last_page: 42 } },
      { id: 'book-catalog-favorites-list', method: 'GET', path: '/store/book-catalog/favorites', type: 'Member', auth: true, desc: 'List books saved as favorites by the authenticated customer.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'book-catalog-favorites-store', method: 'POST', path: '/store/book-catalog/favorites/{bookId}', type: 'Member', auth: true, desc: 'Save a book as favorite. Idempotent.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'book-catalog-favorites-destroy', method: 'DELETE', path: '/store/book-catalog/favorites/{bookId}', type: 'Member', auth: true, desc: 'Remove a book from favorites. Idempotent.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'book-catalog-review-me', method: 'GET', path: '/store/book-catalog/books/{bookId}/reviews/me', type: 'Member', auth: true, desc: 'The authenticated customer\'s own review for a book, whatever its moderation status.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'book-catalog-review-store', method: 'POST', path: '/store/book-catalog/books/{bookId}/reviews', type: 'Member', auth: true, desc: 'Submit a 1-5 star review with an optional comment (one per user per book). Stays pending until the tenant approves it; approval awards Loyalty Card points once if a "Book review" rule is configured.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { rating: 5, comment: 'Bellissimo, l\'ho letto tutto d\'un fiato.' } },
    ],
  },
  {
    name: 'Folders',
    icon: 'tabler-folders',
    endpoints: [
      { id: 'folders-list', method: 'GET', path: '/store/folders', type: 'Guest', desc: 'Active folder trees with their direct item count, for the picker screen when an app has more than one.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'folders-show', method: 'GET', path: '/store/folders/{slug}', type: 'Guest', desc: 'The ENTIRE tree in one response (same pattern as Funnel/Gallery): the app filters by parent_id to navigate and searches client-side, no round-trip per opened sub-folder.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
    ],
  },
  {
    name: 'Digital Signage Kiosk',
    icon: 'tabler-device-tv',
    endpoints: [
      { id: 'signage-kiosk-content', method: 'GET', auth: true, path: '/store/digital-signage/kiosk', type: 'Staff', desc: 'Full layout (zones + items) for the display owning the token below. Called by the Flutter/PWA app running in kiosk mode, not by a regular customer.', headers: [{ key: 'Authorization', value: 'Bearer {{DISPLAY_TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'signage-kiosk-rss', method: 'GET', auth: true, path: '/store/digital-signage/kiosk/rss?item_id={itemId}', type: 'Staff', desc: 'Server-side fetch of the RSS feed URL configured on an rss_feed widget item (the feed is an arbitrary external URL, so it can\'t be fetched client-side).', headers: [{ key: 'Authorization', value: 'Bearer {{DISPLAY_TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'signage-kiosk-gallery', method: 'GET', auth: true, path: '/store/digital-signage/kiosk/gallery?item_id={itemId}', type: 'Staff', desc: 'Resolved payload for a gallery-type widget item.', headers: [{ key: 'Authorization', value: 'Bearer {{DISPLAY_TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'signage-kiosk-weather', method: 'GET', auth: true, path: '/store/digital-signage/kiosk/weather?item_id={itemId}', type: 'Staff', desc: 'Resolved payload for a weather-type widget item (Open-Meteo, server-side).', headers: [{ key: 'Authorization', value: 'Bearer {{DISPLAY_TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
    ],
  },
  {
    name: 'Radio World',
    icon: 'tabler-radio',
    endpoints: [
      { id: 'radio-world-countries', method: 'GET', path: '/store/radio/world/countries', type: 'Guest', desc: 'Country list for the "World" mode browser, proxied server-side from radio-browser.info - never called directly by the client.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'radio-world-tags', method: 'GET', path: '/store/radio/world/tags', type: 'Guest', desc: 'Genre/tag list for the "World" mode browser, proxied server-side from radio-browser.info.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'radio-world-stations', method: 'GET', path: '/store/radio/world/stations?country={country}&tag={tag}&name={query}&limit={limit}', type: 'Guest', desc: 'Search worldwide stations by country, tag and/or name (radio-browser.info, server-side proxy, up to 100 results).', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'radio-world-station-click', method: 'POST', path: '/store/radio/world/stations/{uuid}/click', type: 'Guest', desc: 'Register a listen/click for a worldwide station, as radio-browser.info expects for its popularity ranking.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
    ],
  },
  {
    name: 'Games',
    icon: 'tabler-device-gamepad-2',
    endpoints: [
      { id: 'games-results-submit', method: 'POST', path: '/store/games/results', type: 'Member', auth: true, desc: 'Submit the outcome of a finished arcade game round (Klondike, FreeCell or Memory). No guest scoring: results are always tied to a logged-in user.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { game_key: 'klondike', score: 1280, moves: 96, duration_seconds: 214, won: true } },
      { id: 'games-leaderboard', method: 'GET', path: '/store/games/leaderboard?game_key={gameKey}', type: 'Member', auth: true, desc: 'Top 20 winning scores for a game (default game_key: klondike), scoped to the current tenant/app.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
    ],
  },
  {
    name: 'CouponRush',
    icon: 'tabler-bolt',
    endpoints: [
      { id: 'coupon-rush-categories', method: 'GET', path: '/store/coupon-rush/categories', type: 'Guest', desc: 'Active store categories shown as filters on the map/list browse screen.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'coupon-rush-stores-search', method: 'GET', path: '/store/coupon-rush/stores?lat={lat}&lng={lng}&radius={radius}&category_id={categoryId}&q={query}', type: 'Guest', desc: 'Browse/search stores. With lat/lng, results are ordered by distance (Haversine, radius in km, default 25) and include a computed "distance" field; without coordinates, results are ordered by verified-first then name.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'coupon-rush-store-show', method: 'GET', path: '/store/coupon-rush/stores/{id}', type: 'Guest', desc: 'Store detail plus its currently available coupons (active, inside the starts_at/expires_at window - sold-out coupons still appear, just flagged). Increments the store view counter.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'coupon-rush-coupon-resolve', method: 'GET', path: '/store/coupon-rush/coupons/resolve/{qrToken}', type: 'Guest', desc: 'Resolve a coupon by its QR token WITHOUT redeeming it - shown as a "want to redeem this?" confirmation before the actual claim.', headers: [{ key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'coupon-rush-application-store', method: 'POST', path: '/store/coupon-rush/store-applications', type: 'Member', auth: true, desc: 'Apply to become a marketplace store (business name, address, phone, category). Address is best-effort geocoded server-side if a Google Maps key is configured.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { business_name: 'Bar Roma', address: 'Piazza Duomo 1, Milano', phone: '3331234567', category_id: 1 } },
      { id: 'coupon-rush-application-me', method: 'GET', path: '/store/coupon-rush/store-applications/me', type: 'Member', auth: true, desc: 'The authenticated user\'s own latest store application and its status (pending/approved/rejected).', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'coupon-rush-my-store-show', method: 'GET', path: '/store/coupon-rush/my-store', type: 'Member', auth: true, desc: 'The store owned by the authenticated user, if their application was approved.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'coupon-rush-my-store-update', method: 'PUT', path: '/store/coupon-rush/my-store', type: 'Member', auth: true, desc: 'Self-service edit of the authenticated user\'s own store showcase.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { name: 'Bar Roma', description: 'Caffetteria nel centro storico', phone: '3331234567', address: 'Piazza Duomo 1, Milano' } },
      { id: 'coupon-rush-my-store-coupons-list', method: 'GET', path: '/store/coupon-rush/my-store/coupons', type: 'Member', auth: true, desc: 'Coupons published by the authenticated user\'s own store.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'coupon-rush-my-store-coupons-store', method: 'POST', path: '/store/coupon-rush/my-store/coupons', type: 'Member', auth: true, desc: 'Publish a new limited-quantity coupon for the authenticated user\'s own store. A unique QR token is generated automatically.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { title: 'Caffè gratis', description: 'Un caffè in omaggio, solo il primo che arriva', total_quantity: 1 } },
      { id: 'coupon-rush-my-store-coupons-update', method: 'PUT', path: '/store/coupon-rush/my-store/coupons/{id}', type: 'Member', auth: true, desc: 'Update a coupon belonging to the authenticated user\'s own store.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { title: 'Caffè gratis', total_quantity: 2, is_active: true } },
      { id: 'coupon-rush-my-store-coupons-destroy', method: 'DELETE', path: '/store/coupon-rush/my-store/coupons/{id}', type: 'Member', auth: true, desc: 'Delete a coupon belonging to the authenticated user\'s own store.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'coupon-rush-redeem', method: 'POST', path: '/store/coupon-rush/coupons/redeem/{qrToken}', type: 'Member', auth: true, desc: 'Atomic redemption by QR token - "first to scan wins". Response status is the source of truth: won / already_redeemed_by_you / sold_out / not_started / expired / not_found.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'coupon-rush-my-redemptions', method: 'GET', path: '/store/coupon-rush/my-redemptions', type: 'Member', auth: true, desc: 'Paginated history of coupons won by the authenticated user.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
    ],
  },
  {
    // Diverso dal resto del catalogo: non endpoint app-facing (Guest/Member/Staff),
    // ma endpoint che il TITOLARE tenant chiama con il proprio token (generato in
    // Impostazioni > API Token) per leggere/gestire i propri dati da un gestionale/
    // CRM esterno. Gli endpoint specifici di un modulo (es. i form) vivono nel
    // gruppo di quel modulo (type:'Server' li distingue comunque in filtro); qui
    // restano solo i trasversali, non legati a un singolo modulo.
    name: 'Backoffice & Integrations (your tenant token)',
    icon: 'tabler-server',
    endpoints: [
      { id: 'backoffice-users-list', method: 'GET', path: '/apps/users', type: 'Server', auth: true, desc: 'List the current app\'s users (customers/staff) - sync your own CRM/gestionale with the tenant\'s user base.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }] },
      { id: 'backoffice-push-create', method: 'POST', path: '/push/create', type: 'Server', auth: true, desc: 'Send a push/email broadcast from your own system via OneSignal targeting - exactly one of include_aliases, filters, or included_segments must be set.', headers: [{ key: 'Authorization', value: 'Bearer {{TOKEN}}' }, { key: 'X-App-Id', value: '{{APP_ID}}' }, { key: 'Content-Type', value: 'application/json' }], body: { headings: { en: 'Flash sale' }, contents: { en: '20% off today only' }, included_segments: ['All'] } },
    ],
  },
]

selectedEndpoint.value = collections[0]?.endpoints[0] ?? null

// Fisarmonica: tutte le collection aperte insieme (21+ collection, 250+
// endpoint) rendevano la pagina un unico scroll infinito. Solo la collection
// dell'endpoint attivo parte aperta; durante una ricerca si aprono tutte
// quelle con risultati, cosi' i match restano visibili senza un click in piu'.
const openCollectionNames = ref<Set<string>>(new Set(collections[0] ? [collections[0].name] : []))

const isCollectionOpen = (name: string) => search.value.trim() !== '' || openCollectionNames.value.has(name)

const toggleCollection = (name: string) => {
  if (search.value.trim() !== '')
    return

  const next = new Set(openCollectionNames.value)
  if (next.has(name)) next.delete(name)
  else next.add(name)
  openCollectionNames.value = next
}

const expandAllCollections = () => {
  openCollectionNames.value = new Set(collections.map(c => c.name))
}

const collapseAllCollections = () => {
  openCollectionNames.value = new Set()
}

try {
  const savedHistory = localStorage.getItem(historyStorageKey)
  requestHistory.value = savedHistory ? JSON.parse(savedHistory) : []
}
catch {
  requestHistory.value = []
}

const filteredCollections = computed(() => {
  const q = search.value.toLowerCase()

  return collections
    .map(c => ({
      ...c,
      endpoints: c.endpoints.filter(e =>
        (search.value
          ? e.path.toLowerCase().includes(q)
            || e.desc.toLowerCase().includes(q)
            || c.name.toLowerCase().includes(q)
          : true)
        && (scopeFilter.value === 'all' || e.type === scopeFilter.value)
        && (authFilter.value === 'all'
          || (authFilter.value === 'auth' ? !!e.auth : !e.auth)),
      ),
    }))
    .filter(c => c.endpoints.length > 0)
})

const totalEndpointCount = computed(() => collections.reduce((total, collection) => total + collection.endpoints.length, 0))

const filteredEndpointCount = computed(() => filteredCollections.value.reduce((total, collection) => total + collection.endpoints.length, 0))
const authRequiredCount = computed(() => collections.reduce((total, collection) => total + collection.endpoints.filter(endpoint => endpoint.auth).length, 0))
const guestProtectedCount = computed(() => collections.reduce((total, collection) => total + collection.endpoints.filter(endpoint => endpoint.type === 'Guest').length, 0))
const appScopedHeaderCount = computed(() => collections.reduce((total, collection) => total + collection.endpoints.filter(endpoint => endpoint.headers.some(header => header.key === 'X-App-Id')).length, 0))
const authOverview = computed(() => [
  {
    label: 'Guest con app token',
    value: String(guestProtectedCount.value),
    icon: 'tabler-shield-lock',
    color: 'success',
  },
  {
    label: 'Bearer richiesto',
    value: String(authRequiredCount.value),
    icon: 'tabler-key',
    color: 'warning',
  },
  {
    label: 'Con X-App-Id',
    value: String(appScopedHeaderCount.value),
    icon: 'tabler-apps',
    color: 'info',
  },
  {
    label: 'Collection',
    value: String(collections.length),
    icon: 'tabler-stack-2',
    color: 'primary',
  },
])

const selectedCollection = computed(() => {
  if (!selectedEndpoint.value)
    return null

  return collections.find(collection => collection.endpoints.some(endpoint => endpoint.id === selectedEndpoint.value?.id)) ?? null
})

const selectedHeaders = computed(() => selectedEndpoint.value?.headers ?? [])
const effectiveSelectedHeaders = computed(() => {
  if (!selectedEndpoint.value)
    return [] as ApiHeader[]

  const headers = [...selectedHeaders.value]

  if (selectedEndpoint.value.type === 'Guest' && !headers.some(header => header.key === 'X-App-Token'))
    headers.unshift({ key: 'X-App-Token', value: '{{APP_PUBLIC_TOKEN}}' })

  return headers
})

const selectedRequestBody = computed(() => {
  if (!selectedEndpoint.value?.body)
    return ''

  return JSON.stringify(selectedEndpoint.value.body, null, 2)
})

const formattedResponse = computed(() => {
  if (lastResponse.value == null)
    return ''

  return typeof lastResponse.value === 'string'
    ? lastResponse.value
    : JSON.stringify(lastResponse.value, null, 2)
})

const responseTopLevelKeys = computed(() => {
  if (!lastResponse.value || typeof lastResponse.value !== 'object' || Array.isArray(lastResponse.value))
    return []

  return Object.keys(lastResponse.value as Record<string, unknown>).slice(0, 12)
})

const responseKindLabel = computed(() => {
  if (lastResponse.value == null)
    return 'Nessuna response'
  if (Array.isArray(lastResponse.value))
    return `Array (${lastResponse.value.length})`
  if (typeof lastResponse.value === 'object')
    return 'JSON Object'
  return 'Raw Text'
})

const placeholderReplacePattern = /{[^}]+}/g
const placeholderDetectPattern = /{[^}]+}/
const headerTokenPattern = /{{([^}]+)}}/g

const hasPathPlaceholders = (path: string) => placeholderDetectPattern.test(path)

const resolveHeaderValue = (value: string) => {
  return value.replace(headerTokenPattern, (_, token: string) => {
    switch (token) {
    case 'APP_ID':
      return globalAppId.value || 'YOUR_APP_ID'
    case 'TOKEN':
      return globalToken.value || 'YOUR_USER_TOKEN'
    case 'APP_PUBLIC_TOKEN':
      return globalAppPublicToken.value || 'YOUR_APP_PUBLIC_TOKEN'
    default:
      return token
    }
  })
}

const resolvedEndpointHeaders = computed(() => {
  if (!selectedEndpoint.value)
    return {} as Record<string, string>

  return effectiveSelectedHeaders.value.reduce<Record<string, string>>((acc, header) => {
    const resolved = resolveHeaderValue(header.value)

    if (header.key === 'Authorization' && !globalToken.value && selectedEndpoint.value?.auth)
      return acc

    if (header.key === 'X-App-Id' && !globalAppId.value)
      return acc

    if (header.key === 'X-App-Token' && !globalAppPublicToken.value)
      return acc

    acc[header.key] = resolved

    return acc
  }, {})
})

const requestWarnings = computed(() => {
  const warnings: string[] = []

  if (selectedEndpoint.value?.headers.some(header => header.key === 'X-App-Id') && !globalAppId.value)
    warnings.push('Imposta `X-App-Id` per eseguire richieste reali contro una app.')

  if (selectedEndpoint.value?.type === 'Guest' && !globalAppPublicToken.value)
    warnings.push('Imposta `X-App-Token` con il token tecnico pubblico della build/app.')

  if (selectedEndpoint.value?.auth && !globalToken.value)
    warnings.push('Questo endpoint richiede un Bearer token utente valido.')

  if (hasPathPlaceholders(editablePath.value))
    warnings.push('Sostituisci tutti i placeholder rimasti nel path prima di inviare la richiesta.')

  return warnings
})

const canSendRequest = computed(() => !isSending.value && !!selectedEndpoint.value && !hasPathPlaceholders(editablePath.value))

const getMethodColor = (method: HttpMethod) => {
  const colorMap: Record<HttpMethod, string> = {
    GET: 'success',
    POST: 'warning',
    PUT: 'info',
    PATCH: 'info',
    DELETE: 'error',
  }

  return colorMap[method]
}

const getScopeColor = (scope: EndpointScope) => {
  const colorMap: Record<EndpointScope, string> = {
    Guest: 'success',
    Member: 'info',
    Staff: 'warning',
    Server: 'secondary',
  }

  return colorMap[scope]
}

const persistHistory = () => {
  localStorage.setItem(historyStorageKey, JSON.stringify(requestHistory.value))
}

const setCopyFeedback = (message: string) => {
  copyFeedback.value = message
  window.clearTimeout((setCopyFeedback as typeof setCopyFeedback & { timer?: number }).timer)
  ;(setCopyFeedback as typeof setCopyFeedback & { timer?: number }).timer = window.setTimeout(() => {
    copyFeedback.value = ''
  }, 1800)
}

const copyText = async (label: string, value: string) => {
  if (!value)
    return

  try {
    await navigator.clipboard.writeText(value)
    setCopyFeedback(`${label} copiato`)
  }
  catch {
    setCopyFeedback(`Impossibile copiare ${label.toLowerCase()}`)
  }
}

const selectHistoryItem = (historyItem: RequestHistoryItem) => {
  const endpoint = collections
    .flatMap(collection => collection.endpoints)
    .find(item => item.id === historyItem.endpointId)

  if (!endpoint)
    return

  selectEndpoint(endpoint)
  editablePath.value = historyItem.path
}

// Stesso ragionamento di settings/token.vue: un tenant whitelabel vede il
// proprio brand anche navigando su www.appforge.it (il brand segue il
// tenant autenticato, non il dominio corrente) - preferiamo quindi il
// dominio del pannello del tenant quando il whitelabel e' attivo, invece
// di rivelare sempre "appforge.it" in mezzo a un pannello brandizzato.
const baseUrl = computed(() => {
  const base = (isWhitelabel.value && platformBaseUrl.value) ? platformBaseUrl.value : window.location.origin

  return `${base}/api`
})

const getProcessedPath = (path: string) => {
  return path.replace(placeholderReplacePattern, globalResourceId.value)
}

if (selectedEndpoint.value && !editablePath.value)
  editablePath.value = getProcessedPath(selectedEndpoint.value.path)

const selectEndpoint = (e: ApiEndpoint) => {
  selectedEndpoint.value = e
  editablePath.value = getProcessedPath(e.path)
  lastResponse.value = null
  lastStatus.value = null
  if (!e.body) activeTab.value = 'headers'
  else activeTab.value = 'body'
  endpointDialogOpen.value = true

  const parentCollection = collections.find(c => c.endpoints.some(endpoint => endpoint.id === e.id))
  if (parentCollection && !openCollectionNames.value.has(parentCollection.name))
    openCollectionNames.value = new Set(openCollectionNames.value).add(parentCollection.name)
}

const resetEditablePath = () => {
  if (!selectedEndpoint.value)
    return

  editablePath.value = getProcessedPath(selectedEndpoint.value.path)
}

const clearResponse = () => {
  lastResponse.value = null
  lastStatus.value = null
  lastDurationMs.value = null
  responseView.value = 'preview'
}

const saveAuth = () => {
  localStorage.setItem('dev_token', globalToken.value)
  localStorage.setItem('dev_app_id', globalAppId.value)
  localStorage.setItem('dev_app_public_token', globalAppPublicToken.value)
  localStorage.setItem('dev_resource_id', globalResourceId.value)
  if (selectedEndpoint.value) {
    editablePath.value = getProcessedPath(selectedEndpoint.value.path)
  }
}

const sendRequest = async () => {
  if (!selectedEndpoint.value) return
  isSending.value = true
  lastResponse.value = null
  lastDurationMs.value = null
  
  const e = selectedEndpoint.value
  const url = `${baseUrl.value}${editablePath.value}`
  const startedAt = performance.now()
  
  try {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...resolvedEndpointHeaders.value,
    }

    if (e.body)
      headers['Content-Type'] = headers['Content-Type'] || 'application/json'

    const res = await fetch(url, {
      method: e.method,
      headers: headers,
      body: e.method !== 'GET' ? JSON.stringify(e.body) : null,
    })

    lastStatus.value = res.status
    const contentType = res.headers.get('content-type') || ''
    const responseText = await res.text()

    if (contentType.includes('application/json')) {
      lastResponse.value = responseText ? JSON.parse(responseText) : null
    }
    else {
      try {
        lastResponse.value = responseText ? JSON.parse(responseText) : null
      }
      catch {
        lastResponse.value = responseText || null
      }
    }
  }
  catch (err: unknown) {
    lastResponse.value = { error: err instanceof Error ? err.message : String(err) }
  }
  finally {
    lastDurationMs.value = Math.round(performance.now() - startedAt)
    responseView.value = 'preview'
    requestHistory.value = [
      {
        id: `${e.id}-${Date.now()}`,
        endpointId: e.id,
        method: e.method,
        path: editablePath.value,
        desc: e.desc,
        collectionName: selectedCollection.value?.name || 'Unknown',
        scope: e.type,
        requiresAuth: !!e.auth,
        status: lastStatus.value,
        durationMs: lastDurationMs.value,
        createdAt: new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      },
      ...requestHistory.value.filter(item => !(item.endpointId === e.id && item.path === editablePath.value)).slice(0, 5),
    ]
    persistHistory()
    isSending.value = false
  }
}

const generateCode = computed(() => {
  if (!selectedEndpoint.value) return ''
  const e = selectedEndpoint.value
  const fullUrl = `${baseUrl.value}${editablePath.value}`
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...Object.fromEntries(
      effectiveSelectedHeaders.value.map(header => [header.key, resolveHeaderValue(header.value)]),
    ),
  }

  if (e.body)
    headers['Content-Type'] = headers['Content-Type'] || 'application/json'

  const snippets: Record<CodeLang, string> = {
    javascript: `fetch("${fullUrl}", {
  method: "${e.method}",
  headers: ${JSON.stringify(headers, null, 2)},
  ${e.body ? `body: JSON.stringify(${JSON.stringify(e.body, null, 2)})` : ''}
}).then(res => res.json()).then(console.log);`,

    php: `<?php
$client = new \\GuzzleHttp\\Client();
$response = $client->request('${e.method}', '${fullUrl}', [
  'headers' => ${JSON.stringify(headers, null, 4).replace(/:/g, ' =>')},
  ${e.body ? `'json' => ${JSON.stringify(e.body, null, 4).replace(/:/g, ' =>')}` : ''}
]);
echo $response->getBody();`,

    nodejs: `const axios = require('axios');
axios({
  method: '${e.method.toLowerCase()}',
  url: '${fullUrl}',
  headers: ${JSON.stringify(headers, null, 2)},
  ${e.body ? `data: ${JSON.stringify(e.body, null, 2)}` : ''}
}).then(res => console.log(res.data));`,
  }
  return snippets[selectedLang.value] || ''
})
</script>

<template>
  <div class="developers-api-page">
    <VContainer fluid class="py-6">
      <VRow>
        <VCol cols="12">
          <VCard class="hero-card overflow-hidden mb-6">
            <div class="hero-surface" />
            <VCardText class="hero-content">
              <div class="hero-main">
                <VAvatar color="white" size="64" class="hero-avatar">
                  <VIcon icon="tabler-api" size="34" color="primary" />
                </VAvatar>

                <div class="min-w-0">
                  <div class="d-flex flex-wrap align-center gap-2 mb-3">
                    <VChip size="small" color="white" variant="outlined" class="hero-chip">
                      APP API
                    </VChip>
                    <VChip size="small" color="white" variant="outlined" class="hero-chip">
                      Guest: X-App-Token
                    </VChip>
                    <VChip size="small" color="white" variant="outlined" class="hero-chip">
                      Member/Staff: Bearer
                    </VChip>
                    <VChip size="small" color="white" variant="outlined" class="hero-chip">
                      Server: your tenant token
                    </VChip>
                  </div>

                  <h1 class="hero-title">Developer API Explorer</h1>
                  <p class="hero-subtitle">
                    Catalogo operativo delle API reali: gli endpoint usati dall app cliente e dai ruoli in-app (Guest/Member/Staff), piu' gli endpoint "Server" con cui il titolare tenant legge i propri dati (es. risposte di un form) col proprio token per integrarli in un gestionale esterno. Escluse le sole API di sistema/piattaforma/whitelabel (provisioning reseller, fatturazione, SMTP/WhatsApp del pannello).
                  </p>

                  <div class="hero-note">
                    <VIcon icon="tabler-info-circle" size="16" />
                    <span>
                      <strong>Guest</strong> richiede <code>X-App-Token</code> e, quando l app e multi-app, anche <code>X-App-Id</code>. <strong>Member</strong>, <strong>Staff</strong> e <strong>Server</strong> usano invece <code>Authorization: Bearer ...</code> piu il contesto app - per <strong>Server</strong> il token e' quello del titolare tenant (generato in Impostazioni &gt; API Token), non quello di un cliente/operatore dell app.
                    </span>
                  </div>
                </div>
              </div>

              <div class="hero-panel">
                <div class="hero-panel-label">Base API URL</div>
                <div class="hero-panel-url">{{ baseUrl }}</div>

                <div class="hero-stats">
                  <div v-for="item in authOverview" :key="item.label" class="hero-stat">
                    <VAvatar :color="item.color" variant="tonal" size="34">
                      <VIcon :icon="item.icon" size="18" />
                    </VAvatar>
                    <div class="hero-stat-copy">
                      <div class="hero-stat-value">{{ item.value }}</div>
                      <div class="hero-stat-label">{{ item.label }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </VCardText>
          </VCard>
        </VCol>

        <VCol cols="12" lg="4">
          <VCard class="premium-card mb-6">
            <VCardText>
              <div class="section-header">
                <VAvatar color="primary" variant="tonal" size="42" rounded="lg">
                  <VIcon icon="tabler-key" />
                </VAvatar>
                <div>
                  <div class="section-title">Credenziali di test</div>
                  <div class="section-subtitle">Inserisci il contesto app e i token da riusare in tutte le richieste reali.</div>
                </div>
              </div>

              <div class="credentials-grid">
                <VTextField v-model="globalAppId" label="X-App-Id" density="comfortable" hide-details class="premium-input" @update:model-value="saveAuth" />
                <VTextField v-model="globalAppPublicToken" label="X-App-Token" density="comfortable" hide-details class="premium-input" type="password" @update:model-value="saveAuth" />
                <VTextField v-model="globalToken" label="Bearer utente" density="comfortable" hide-details class="premium-input" type="password" @update:model-value="saveAuth" />
                <VTextField v-model="globalResourceId" label="Placeholder ID" density="comfortable" hide-details class="premium-input" @update:model-value="saveAuth" />
              </div>

              <div class="summary-grid mt-4">
                <div class="summary-pill">
                  <span>Collection filtrate</span>
                  <strong>{{ filteredCollections.length }}</strong>
                </div>
                <div class="summary-pill">
                  <span>Endpoint</span>
                  <strong>{{ filteredEndpointCount }}/{{ totalEndpointCount }}</strong>
                </div>
              </div>

              <div class="d-flex flex-wrap gap-2 mt-4">
                <VBtn variant="tonal" color="primary" prepend-icon="tabler-home" to="/">
                  Home
                </VBtn>
                <VBtn variant="tonal" prepend-icon="tabler-copy" @click="copyText('Base API URL', baseUrl)">
                  Copia base URL
                </VBtn>
              </div>
            </VCardText>
          </VCard>

          <VCard class="premium-card mb-6">
            <VCardText>
              <div class="section-header">
                <VAvatar color="info" variant="tonal" size="42" rounded="lg">
                  <VIcon icon="tabler-filter" />
                </VAvatar>
                <div>
                  <div class="section-title">Filtri</div>
                  <div class="section-subtitle">Riduci il catalogo per ruolo, autenticazione e testo libero.</div>
                </div>
              </div>

              <VTextField
                v-model="search"
                placeholder="Cerca path, descrizione o collection"
                density="comfortable"
                prepend-inner-icon="tabler-search"
                class="premium-input mb-4"
                hide-details
              />

              <div class="filter-block">
                <div class="filter-label">Scope</div>
                <div class="d-flex flex-wrap gap-2">
                  <VBtn size="small" :variant="scopeFilter === 'all' ? 'flat' : 'tonal'" :color="scopeFilter === 'all' ? 'primary' : 'secondary'" @click="scopeFilter = 'all'">All</VBtn>
                  <VBtn size="small" :variant="scopeFilter === 'Guest' ? 'flat' : 'tonal'" :color="scopeFilter === 'Guest' ? 'success' : 'secondary'" @click="scopeFilter = 'Guest'">Guest</VBtn>
                  <VBtn size="small" :variant="scopeFilter === 'Member' ? 'flat' : 'tonal'" :color="scopeFilter === 'Member' ? 'info' : 'secondary'" @click="scopeFilter = 'Member'">Member</VBtn>
                  <VBtn size="small" :variant="scopeFilter === 'Staff' ? 'flat' : 'tonal'" :color="scopeFilter === 'Staff' ? 'warning' : 'secondary'" @click="scopeFilter = 'Staff'">Staff</VBtn>
                  <VBtn size="small" :variant="scopeFilter === 'Server' ? 'flat' : 'tonal'" color="secondary" @click="scopeFilter = 'Server'">Server</VBtn>
                </div>
              </div>

              <div class="filter-block">
                <div class="filter-label">Autenticazione</div>
                <div class="d-flex flex-wrap gap-2">
                  <VBtn size="small" :variant="authFilter === 'all' ? 'flat' : 'tonal'" :color="authFilter === 'all' ? 'primary' : 'secondary'" @click="authFilter = 'all'">Any Auth</VBtn>
                  <VBtn size="small" :variant="authFilter === 'auth' ? 'flat' : 'tonal'" :color="authFilter === 'auth' ? 'warning' : 'secondary'" @click="authFilter = 'auth'">Auth Only</VBtn>
                  <VBtn size="small" :variant="authFilter === 'open' ? 'flat' : 'tonal'" :color="authFilter === 'open' ? 'success' : 'secondary'" @click="authFilter = 'open'">Guest/App Token</VBtn>
                </div>
              </div>
            </VCardText>
          </VCard>

          <VCard class="premium-card">
            <VCardText>
              <div class="section-header section-header--split">
                <div class="d-flex align-center gap-3 min-w-0">
                  <VAvatar color="secondary" variant="tonal" size="42" rounded="lg">
                    <VIcon icon="tabler-history" />
                  </VAvatar>
                  <div>
                    <div class="section-title">Ultime richieste</div>
                    <div class="section-subtitle">Riapplica rapidamente gli endpoint che hai gia testato.</div>
                  </div>
                </div>
                <VChip size="small" color="secondary" variant="tonal">
                  {{ requestHistory.length }}
                </VChip>
              </div>

              <div v-if="requestHistory.length" class="history-list">
                <button v-for="item in requestHistory" :key="item.id" type="button" class="history-item" @click="selectHistoryItem(item)">
                  <div class="history-topline">
                    <span class="method-chip" :class="`method-chip--${item.method.toLowerCase()}`">{{ item.method }}</span>
                    <span class="history-status" :class="item.status !== null && item.status < 400 ? 'history-status--ok' : 'history-status--ko'">{{ item.status ?? '--' }}</span>
                  </div>
                  <div class="history-path">{{ item.path }}</div>
                  <div class="history-meta">
                    <span>{{ item.collectionName }}</span>
                    <span>{{ item.createdAt }}</span>
                    <span v-if="item.durationMs !== null">{{ item.durationMs }} ms</span>
                  </div>
                </button>
              </div>
              <div v-else class="empty-panel empty-panel--compact">
                <VIcon icon="tabler-history-off" size="24" class="mb-1" />
                <div>Le richieste inviate compariranno qui.</div>
              </div>
            </VCardText>
          </VCard>
        </VCol>

        <VCol cols="12" lg="8">
          <VCard class="premium-card mb-6">
            <VCardText>
              <div class="section-header section-header--split">
                <div class="d-flex align-center gap-3 min-w-0">
                  <VAvatar color="success" variant="tonal" size="42" rounded="lg">
                    <VIcon icon="tabler-apps" />
                  </VAvatar>
                  <div>
                    <div class="section-title">Catalogo endpoint</div>
                    <div class="section-subtitle">Seleziona una collection e poi l endpoint da provare nel pannello operativo.</div>
                  </div>
                </div>
                <div class="d-flex align-center gap-2 flex-wrap">
                  <VChip size="small" color="success" variant="tonal">
                    {{ filteredEndpointCount }} endpoint
                  </VChip>
                  <VBtn variant="text" size="small" :disabled="!!search.trim()" @click="expandAllCollections">
                    Espandi tutte
                  </VBtn>
                  <VBtn variant="text" size="small" :disabled="!!search.trim()" @click="collapseAllCollections">
                    Comprimi tutte
                  </VBtn>
                </div>
              </div>

              <div v-if="filteredCollections.length" class="collection-stack">
                <div v-for="c in filteredCollections" :key="c.name" class="collection-card">
                  <button
                    type="button"
                    class="collection-card__header"
                    :aria-expanded="isCollectionOpen(c.name)"
                    @click="toggleCollection(c.name)"
                  >
                    <div class="collection-card__title">
                      <VIcon icon="tabler-folder" size="18" class="me-2" />
                      {{ c.name }}
                    </div>
                    <div class="d-flex align-center gap-2">
                      <VChip size="x-small" color="primary" variant="tonal">
                        {{ c.endpoints.length }}
                      </VChip>
                      <VIcon
                        icon="tabler-chevron-down"
                        size="18"
                        class="collection-card__chevron"
                        :class="{ 'collection-card__chevron--open': isCollectionOpen(c.name) }"
                      />
                    </div>
                  </button>

                  <div v-show="isCollectionOpen(c.name)" class="endpoint-grid">
                    <button
                      v-for="e in c.endpoints"
                      :key="e.id"
                      type="button"
                      class="endpoint-option"
                      :class="{ 'endpoint-option--active': selectedEndpoint?.id === e.id }"
                      @click="selectEndpoint(e)"
                    >
                      <div class="endpoint-option__top">
                        <span class="method-chip" :class="`method-chip--${e.method.toLowerCase()}`">{{ e.method }}</span>
                        <div class="d-flex align-center gap-1 flex-wrap justify-end">
                          <VChip size="x-small" :color="getScopeColor(e.type)" variant="tonal">{{ e.type }}</VChip>
                          <VChip v-if="e.auth" size="x-small" color="warning" variant="tonal">Bearer</VChip>
                          <VChip v-else size="x-small" color="success" variant="tonal">App Token</VChip>
                        </div>
                      </div>
                      <div class="endpoint-option__path">{{ e.path }}</div>
                      <div class="endpoint-option__desc">{{ e.desc }}</div>
                    </button>
                  </div>
                </div>
              </div>

              <div v-else class="empty-panel">
                <VIcon icon="tabler-search-off" size="28" class="mb-2" />
                <div>Nessun endpoint corrisponde alla ricerca corrente.</div>
              </div>
            </VCardText>
          </VCard>

          <VDialog v-model="endpointDialogOpen" max-width="960" scrollable>
            <VCard v-if="selectedEndpoint" class="premium-card">
            <VCardText>
              <div class="section-header section-header--split">
                <div class="min-w-0">
                  <div class="d-flex align-center flex-wrap gap-2 mb-3">
                    <VChip size="small" :color="getScopeColor(selectedEndpoint.type)" variant="flat">{{ selectedEndpoint.type }} API</VChip>
                    <VChip size="small" :color="getMethodColor(selectedEndpoint.method)" variant="tonal">{{ selectedEndpoint.method }}</VChip>
                    <VChip size="small" color="primary" variant="tonal">{{ selectedCollection?.name }}</VChip>
                    <VChip v-if="selectedEndpoint.auth" size="small" color="warning" variant="tonal">Auth Required</VChip>
                    <VChip v-else size="small" color="success" variant="tonal">App Token</VChip>
                    <VChip v-if="selectedEndpoint.headers.some(header => header.key === 'X-App-Id')" size="small" color="info" variant="tonal">X-App-Id</VChip>
                  </div>
                  <div class="section-title">Endpoint attivo</div>
                  <div class="endpoint-title">{{ selectedEndpoint.desc }}</div>
                </div>

                <div class="d-flex flex-wrap gap-2 justify-end">
                  <VBtn variant="tonal" size="small" prepend-icon="tabler-copy" @click="copyText('URL', `${baseUrl}${editablePath}`)">Copia URL</VBtn>
                  <VBtn variant="tonal" size="small" prepend-icon="tabler-restore" @click="resetEditablePath">Reset Path</VBtn>
                  <VBtn variant="text" size="small" prepend-icon="tabler-eraser" @click="clearResponse">Pulisci response</VBtn>
                  <VBtn icon variant="text" size="small" @click="endpointDialogOpen = false">
                    <VIcon icon="tabler-x" />
                  </VBtn>
                </div>
              </div>

              <div class="base-url-card mb-4">
                <div class="text-caption opacity-70 mb-2">URL completo</div>
                <div class="inline-code endpoint-path-preview">{{ baseUrl }}{{ editablePath }}</div>
              </div>

              <div v-if="copyFeedback" class="copy-feedback">{{ copyFeedback }}</div>

              <div v-if="requestWarnings.length" class="warnings-list">
                <div v-for="warning in requestWarnings" :key="warning" class="warning-item">
                  <VIcon icon="tabler-alert-triangle" size="16" />
                  <span>{{ warning }}</span>
                </div>
              </div>

              <div class="request-composer">
                <div class="method-box" :class="`method-box--${selectedEndpoint.method.toLowerCase()}`">
                  {{ selectedEndpoint.method }}
                </div>
                <div class="url-prefix">{{ baseUrl }}</div>
                <input v-model="editablePath" class="url-input" placeholder="/path/to/resource" @keydown.enter="sendRequest" />
                <VBtn color="primary" size="large" class="send-btn" :loading="isSending" :disabled="!canSendRequest" @click="sendRequest">
                  SEND
                </VBtn>
              </div>

              <div class="tabs-header mt-6">
                <button type="button" class="tab-button" :class="{ 'tab-button--active': activeTab === 'body' }" @click="activeTab = 'body'">Body</button>
                <button type="button" class="tab-button" :class="{ 'tab-button--active': activeTab === 'headers' }" @click="activeTab = 'headers'">Headers</button>
                <button type="button" class="tab-button" :class="{ 'tab-button--active': activeTab === 'code' }" @click="activeTab = 'code'">Code Snippets</button>
              </div>

              <div class="tab-surface mt-4">
                <div v-if="activeTab === 'code'">
                  <div class="panel-toolbar">
                    <div class="d-flex gap-2 flex-wrap">
                      <VBtn
                        v-for="lang in snippetLanguages"
                        :key="lang.key"
                        size="small"
                        :variant="selectedLang === lang.key ? 'flat' : 'tonal'"
                        :color="selectedLang === lang.key ? 'primary' : 'secondary'"
                        @click="setSelectedLang(lang.key)"
                      >
                        {{ lang.label }}
                      </VBtn>
                    </div>
                    <VBtn variant="tonal" size="small" prepend-icon="tabler-copy" @click="copyText('Snippet', generateCode)">Copia snippet</VBtn>
                  </div>
                  <div class="code-block">
                    <pre><code>{{ generateCode }}</code></pre>
                  </div>
                </div>

                <div v-else-if="activeTab === 'body'">
                  <div v-if="selectedEndpoint.body" class="panel-toolbar">
                    <div class="panel-title">Payload di esempio</div>
                    <VBtn variant="tonal" size="small" prepend-icon="tabler-copy" @click="copyText('Payload JSON', selectedRequestBody)">Copia JSON</VBtn>
                  </div>
                  <div v-if="selectedEndpoint.body" class="code-block">
                    <div class="editor-header">Request Payload (JSON)</div>
                    <pre><code>{{ selectedRequestBody }}</code></pre>
                  </div>
                  <div v-else class="empty-panel">
                    <VIcon icon="tabler-braces-off" size="28" class="mb-2" />
                    <div>No request body required.</div>
                  </div>
                </div>

                <div v-else>
                  <div class="panel-toolbar">
                    <div class="panel-title">Header richiesti</div>
                    <VBtn variant="tonal" size="small" prepend-icon="tabler-copy" @click="copyText('Headers JSON', JSON.stringify(effectiveSelectedHeaders, null, 2))">Copia headers</VBtn>
                  </div>

                  <div class="headers-note">
                    <VIcon icon="tabler-shield-lock" size="16" />
                    <span>
                      <strong>{{ selectedEndpoint.auth ? 'Endpoint protetto' : 'Endpoint guest protetto' }}</strong>:
                      <template v-if="selectedEndpoint.auth"> usa sempre <code v-pre>Authorization: Bearer {{TOKEN}}</code></template>
                      <template v-else> usa sempre <code v-pre>X-App-Token: {{APP_PUBLIC_TOKEN}}</code></template>
                      <template v-if="selectedEndpoint.headers.some(header => header.key === 'X-App-Id')"> e richiede anche <code>X-App-Id</code></template>.
                    </span>
                  </div>

                  <VTable density="comfortable">
                    <thead>
                      <tr>
                        <th>Header</th>
                        <th>Required Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="h in effectiveSelectedHeaders" :key="h.key">
                        <td><code>{{ h.key }}</code></td>
                        <td><code>{{ h.value }}</code></td>
                      </tr>
                    </tbody>
                  </VTable>
                </div>
              </div>
            </VCardText>
          </VCard>

          <VCard class="premium-card mt-6">
            <VCardText>
              <div class="section-header section-header--split">
                <div class="d-flex align-center gap-3 min-w-0">
                  <VAvatar color="warning" variant="tonal" size="42" rounded="lg">
                    <VIcon icon="tabler-server-2" />
                  </VAvatar>
                  <div>
                    <div class="section-title">Real Response</div>
                    <div class="section-subtitle">Verifica subito status, tempo e payload restituito dall endpoint selezionato.</div>
                  </div>
                </div>

                <div class="response-meta">
                  <span class="summary-pill summary-pill--inline">
                    <span>Status</span>
                    <strong :class="lastStatus !== null && lastStatus < 400 ? 'text-success' : lastStatus !== null ? 'text-error' : ''">{{ lastStatus ?? '--' }}</strong>
                  </span>
                  <span class="summary-pill summary-pill--inline">
                    <span>Tempo</span>
                    <strong>{{ lastDurationMs !== null ? `${lastDurationMs} ms` : '--' }}</strong>
                  </span>
                  <VBtn v-if="lastResponse !== null" variant="tonal" size="small" prepend-icon="tabler-copy" @click="copyText('Response', formattedResponse)">Copia</VBtn>
                </div>
              </div>

              <div v-if="lastResponse !== null" class="tabs-header mb-4">
                <button type="button" class="tab-button" :class="{ 'tab-button--active': responseView === 'preview' }" @click="responseView = 'preview'">Preview</button>
                <button type="button" class="tab-button" :class="{ 'tab-button--active': responseView === 'raw' }" @click="responseView = 'raw'">Raw</button>
              </div>

              <div v-if="lastResponse !== null && responseView === 'preview'" class="response-preview">
                <div class="summary-grid">
                  <div class="summary-pill">
                    <span>Formato</span>
                    <strong>{{ responseKindLabel }}</strong>
                  </div>
                  <div class="summary-pill">
                    <span>Status</span>
                    <strong>{{ lastStatus ?? '--' }}</strong>
                  </div>
                  <div class="summary-pill">
                    <span>Tempo</span>
                    <strong>{{ lastDurationMs !== null ? `${lastDurationMs} ms` : '--' }}</strong>
                  </div>
                  <div class="summary-pill">
                    <span>Collection</span>
                    <strong>{{ selectedCollection?.name }}</strong>
                  </div>
                </div>

                <div v-if="responseTopLevelKeys.length" class="preview-section">
                  <div class="panel-title">Top-level keys</div>
                  <div class="preview-chips">
                    <VChip v-for="key in responseTopLevelKeys" :key="key" size="small" variant="tonal" color="primary">{{ key }}</VChip>
                  </div>
                </div>

                <div class="preview-section">
                  <div class="panel-title">Estratto rapido</div>
                  <div class="code-block">
                    <pre><code>{{ formattedResponse }}</code></pre>
                  </div>
                </div>
              </div>

              <div v-else-if="lastResponse !== null" class="code-block">
                <pre><code>{{ formattedResponse }}</code></pre>
              </div>

              <div v-else class="empty-panel">
                <VIcon icon="tabler-send-2" size="28" class="mb-2" />
                <div>Inserisci credenziali e clicca su SEND per vedere la response reale.</div>
              </div>
            </VCardText>
          </VCard>
          </VDialog>
        </VCol>
      </VRow>
    </VContainer>
  </div>
</template>

<style lang="scss" scoped>
.developers-api-page {
  min-height: 100vh;
  background:
    radial-gradient(circle at top left, rgba(var(--v-theme-primary), 0.12), transparent 26%),
    linear-gradient(180deg, rgba(var(--v-theme-surface), 1) 0%, rgba(var(--v-theme-background), 1) 100%);
}

.hero-card {
  position: relative;
  border: 0;
  border-radius: 22px;
  background: rgb(var(--v-theme-primary));
}

.hero-surface {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 15% 15%, rgba(255, 255, 255, 0.22), transparent 28%),
    linear-gradient(135deg, rgb(var(--v-theme-primary)) 0%, #1f9d8a 52%, #252b4a 100%);
}

.hero-content {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 28px;
  align-items: center;
  padding: 30px;
}

.hero-main {
  display: flex;
  align-items: center;
  gap: 22px;
  min-width: 0;
}

.hero-avatar {
  border: 4px solid rgba(255, 255, 255, 0.22);
  flex: 0 0 auto;
}

.hero-chip {
  color: #fff;
  border-color: rgba(255, 255, 255, 0.35) !important;
}

.hero-title {
  color: #fff;
  font-size: 2.1rem;
  line-height: 1.1;
  margin: 0;
  font-weight: 800;
}

.hero-subtitle {
  color: rgba(255, 255, 255, 0.86);
  margin: 12px 0 0;
  max-width: 760px;
}

.hero-note {
  display: inline-flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 18px;
  padding: 12px 14px;
  border-radius: 16px;
  background: rgba(15, 23, 42, 0.26);
  border: 1px solid rgba(255, 255, 255, 0.16);
  color: rgba(255, 255, 255, 0.9);
  max-width: 900px;
}

.hero-note code {
  color: #fff;
}

.hero-panel {
  padding: 18px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(10px);
}

.hero-panel-label {
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.hero-panel-url {
  color: #fff;
  font-weight: 700;
  margin-block: 8px 18px;
  overflow-wrap: anywhere;
}

.hero-stats {
  display: grid;
  gap: 10px;
}

.hero-stat {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.08);
}

.hero-stat-copy {
  min-width: 0;
}

.hero-stat-value {
  color: #fff;
  font-size: 1.05rem;
  font-weight: 800;
  line-height: 1;
}

.hero-stat-label {
  color: rgba(255, 255, 255, 0.76);
  font-size: 0.8rem;
  margin-top: 2px;
}

.premium-card {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 18px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 30px -20px rgba(var(--v-shadow-key-umbra-color), 0.35);
  }
}

.premium-input :deep(.v-field) {
  border-radius: 14px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-block-end: 18px;
}

.section-header--split {
  justify-content: space-between;
}

.section-title {
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  opacity: 0.76;
}

.section-subtitle {
  font-size: 0.95rem;
  margin-top: 4px;
  opacity: 0.76;
}

.credentials-grid,
.summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.summary-pill {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 16px;
  background: rgba(var(--v-theme-surface), 1);
  font-size: 0.78rem;
  opacity: 0.84;
}

.summary-pill strong {
  font-size: 1rem;
  opacity: 1;
}

.summary-pill--inline {
  min-inline-size: 96px;
}

.filter-block + .filter-block {
  margin-top: 18px;
}

.filter-label {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  opacity: 0.72;
  margin-bottom: 10px;
  text-transform: uppercase;
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.history-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  padding: 12px 14px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 16px;
  background: rgba(var(--v-theme-surface), 1);
  text-align: left;
  color: inherit;
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.history-item:hover {
  transform: translateY(-2px);
  border-color: rgba(var(--v-theme-primary), 0.3);
  box-shadow: 0 14px 30px -24px rgba(var(--v-shadow-key-umbra-color), 0.35);
}

.history-topline,
.collection-card__header,
.panel-toolbar,
.response-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.history-path,
.endpoint-option__path,
.endpoint-path-preview,
.url-prefix,
.url-input,
.method-box,
.inline-code,
code,
pre {
  font-family: 'Fira Code', monospace;
}

.history-path {
  overflow-wrap: anywhere;
  font-size: 0.8rem;
}

.history-meta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 0.72rem;
  opacity: 0.68;
}

.history-status {
  font-size: 0.78rem;
  font-weight: 700;
}

.history-status--ok {
  color: rgb(var(--v-theme-success));
}

.history-status--ko {
  color: rgb(var(--v-theme-error));
}

.collection-stack {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.collection-card,
.base-url-card,
.tab-surface {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 16px;
  padding: 16px;
  background: rgba(var(--v-theme-surface), 1);
}

.collection-card__header {
  inline-size: 100%;
  border: 0;
  background: none;
  padding: 0;
  color: inherit;
  font: inherit;
  text-align: start;
  cursor: pointer;
}

.collection-card__title {
  display: inline-flex;
  align-items: center;
  font-weight: 700;
}

.collection-card__chevron {
  transition: transform 0.2s ease;
  opacity: 0.6;
}

.collection-card__chevron--open {
  transform: rotate(180deg);
}

.endpoint-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
  margin-top: 14px;
}

.endpoint-option {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
  padding: 16px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 16px;
  background: rgba(var(--v-theme-surface), 1);
  cursor: pointer;
  text-align: start;
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.endpoint-option:hover,
.endpoint-option--active {
  transform: translateY(-2px);
  border-color: rgba(var(--v-theme-primary), 0.42);
  box-shadow: 0 14px 30px -24px rgba(var(--v-shadow-key-umbra-color), 0.45);
}

.endpoint-option__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
}

.endpoint-option__desc {
  font-size: 0.84rem;
  opacity: 0.72;
}

.method-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 56px;
  padding: 0.3rem 0.5rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  background: rgba(var(--v-theme-on-surface), 0.08);
}

.method-chip--get,
.method-box--get {
  color: rgb(var(--v-theme-success));
}

.method-chip--post,
.method-box--post {
  color: rgb(var(--v-theme-warning));
}

.method-chip--put,
.method-chip--patch,
.method-box--put,
.method-box--patch {
  color: rgb(var(--v-theme-info));
}

.method-chip--delete,
.method-box--delete {
  color: rgb(var(--v-theme-error));
}

.endpoint-title {
  font-size: 1.15rem;
  font-weight: 700;
  margin-top: 4px;
}

.inline-code {
  display: inline-block;
  padding: 0.45rem 0.65rem;
  border-radius: 10px;
  background: rgba(var(--v-theme-primary), 0.08);
  overflow-wrap: anywhere;
}

.copy-feedback {
  display: inline-flex;
  align-items: center;
  margin-bottom: 12px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(var(--v-theme-success), 0.12);
  color: rgb(var(--v-theme-success));
  font-size: 0.8rem;
  border: 1px solid rgba(var(--v-theme-success), 0.22);
}

.warnings-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 14px;
}

.warning-item,
.headers-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 14px;
  font-size: 0.84rem;
}

.warning-item {
  background: rgba(var(--v-theme-warning), 0.08);
  border: 1px solid rgba(var(--v-theme-warning), 0.18);
  color: rgb(var(--v-theme-warning));
}

.headers-note {
  margin-bottom: 14px;
  background: rgba(var(--v-theme-info), 0.08);
  border: 1px solid rgba(var(--v-theme-info), 0.16);
}

.request-composer {
  display: grid;
  grid-template-columns: auto auto minmax(0, 1fr) auto;
  align-items: stretch;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 18px;
  overflow: hidden;
  background: rgba(var(--v-theme-surface), 1);
}

.method-box,
.url-prefix {
  display: flex;
  align-items: center;
  padding: 0 16px;
  background: rgba(var(--v-theme-on-surface), 0.04);
  border-inline-end: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  font-size: 0.85rem;
  font-weight: 700;
}

.url-prefix {
  opacity: 0.72;
}

.url-input {
  min-width: 0;
  border: 0;
  outline: 0;
  padding: 0 16px;
  background: transparent;
  font-size: 0.88rem;
}

.send-btn {
  border-radius: 0;
  min-height: 56px;
  padding-inline: 28px;
}

.tabs-header {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.tab-button {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 999px;
  padding: 0.55rem 0.9rem;
  background: rgba(var(--v-theme-surface), 1);
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease, transform 0.2s ease;
}

.tab-button--active {
  color: rgb(var(--v-theme-primary));
  border-color: rgba(var(--v-theme-primary), 0.35);
  background: rgba(var(--v-theme-primary), 0.08);
}

.panel-title {
  font-size: 0.85rem;
  font-weight: 700;
}

.code-block {
  background: #121826;
  color: #fff;
  border-radius: 14px;
  overflow-x: auto;
}

.editor-header {
  padding: 0.7rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 0.76rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  opacity: 0.7;
}

pre {
  margin: 0;
  font-size: 13px;
  white-space: pre-wrap;
  word-break: break-word;
  padding: 1rem;
}

.response-preview,
.preview-section {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.preview-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.empty-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  text-align: center;
  min-height: 180px;
  color: rgba(var(--v-theme-on-surface), 0.58);
  font-size: 0.9rem;
  border: 1px dashed rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 16px;
  background: rgba(var(--v-theme-surface), 1);
}

.empty-panel--compact {
  min-height: 120px;
}

@media (max-width: 1279px) {
  .hero-content {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 959px) {
  .credentials-grid,
  .summary-grid {
    grid-template-columns: 1fr;
  }

  .request-composer {
    grid-template-columns: 1fr;
  }

  .method-box,
  .url-prefix,
  .url-input {
    min-height: 48px;
    border-inline-end: 0;
    border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  }
}

@media (max-width: 767px) {
  .hero-content {
    padding: 22px;
  }

  .hero-main {
    flex-direction: column;
    align-items: flex-start;
  }

  .hero-title {
    font-size: 1.7rem;
  }

  .section-header,
  .section-header--split {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
